import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import styled, {keyframes, css} from "styled-components";
import {useSearchParams} from "react-router-dom";
import {db, auth, googleProvider} from "../firebase";
import {signInWithPopup, signOut, onAuthStateChanged} from "firebase/auth";
import {ref, push, onValue, query, orderByChild, limitToLast, get, set, equalTo} from "firebase/database";
import {DILEMA_LOGO_URL} from "../dilemaLogoUrl";
import {
  CHALLENGE_META_REF,
  CHALLENGE_FRIEND_REF,
  addMyChallengeId,
  loadMyChallengeIds,
  mergeAnonMyChallengesIntoUid,
  avgMsFromRounds,
  compareChallengeRuns,
  buildChallengeLink,
  AVATAR_EMOJIS,
} from "./tapOrSkipChallengeHelpers";
import {BULK_DILEMMAS} from "./tapOrSkipDilemmasBulk";

const LOCAL_PROFILE_KEY = "dilemma_profile_v1";
const LOCAL_SETTINGS_KEY = "dilemma_settings_v1";

const DEFAULT_DILEMMAS = [
  {prompt: "פיצה או סושי?", a: "🍕 פיצה", b: "🍣 סושי"},
  {prompt: "טיקטוק או אינסטגרם?", a: "📱 טיקטוק", b: "📸 אינסטגרם"},
  {prompt: "לישון עד מאוחר או לקום מוקדם?", a: "🌙 לישון", b: "☀️ לקום מוקדם"},
  {prompt: "כלב או חתול?", a: "🐶 כלב", b: "🐱 חתול"},
  {prompt: "ים או הרים?", a: "🏖️ ים", b: "🏔️ הרים"},
  {prompt: "נטפליקס או יוטיוב?", a: "🎬 נטפליקס", b: "📺 יוטיוב"},
  {prompt: "קפה או תה?", a: "☕ קפה", b: "🍵 תה"},
  {prompt: "חורף או קיץ?", a: "❄️ חורף", b: "🔥 קיץ"},
  {prompt: "להתקשר או הודעה?", a: "📞 להתקשר", b: "💬 הודעה"},
  {prompt: "גיימינג או מוזיקה?", a: "🎮 גיימינג", b: "🎵 מוזיקה"},
  {prompt: "המבורגר או טאקו?", a: "🍔 המבורגר", b: "🌮 טאקו"},
  {prompt: "ספר או סרט?", a: "📖 ספר", b: "🎥 סרט"},
  {prompt: "לשיר או לרקוד?", a: "🎤 לשיר", b: "💃 לרקוד"},
  {prompt: "חו״ל או טיול בארץ?", a: "✈️ חו״ל", b: "🏕️ ארץ"},
  {prompt: "מסיבה או ערב משחקים?", a: "🎉 מסיבה", b: "🎲 משחקים"},
  {prompt: "מזגן או שמש?", a: "🌬️ מזגן", b: "☀️ שמש"},
  {prompt: "חול או אספלט?", a: "🏖️ חול", b: "🛣️ אספלט"},
  {prompt: "ברגל או באופניים?", a: "🚶 ברגל", b: "🚲 אופניים"},
  {prompt: "סרט או סדרה?", a: "🎬 סרט", b: "📺 סדרה"},
  {prompt: "דרמה או מתח?", a: "🎭 דרמה", b: "😱 מתח"},
  {prompt: "צהוב או ירוק?", a: "🟡 צהוב", b: "🟢 ירוק"},
  {prompt: "בישיבה או בעמידה?", a: "🪑 בישיבה", b: "🧍 בעמידה"},
  {prompt: "בספינה או מטוס?", a: "🚢 ספינה", b: "✈️ מטוס"},
  {prompt: "מכנס קצר או מכנס ארוך?", a: "🩳 קצר", b: "👖 ארוך"},
  {prompt: "עם צ׳אט או עם מחשבה?", a: "💬 צ׳אט", b: "🧠 מחשבה"},
  {prompt: "סרט או באולינג?", a: "🎬 סרט", b: "🎳 באולינג"},
  {prompt: "שעון מחוגים או שעון דיגיטלי?", a: "🕐 מחוגים", b: "⌚ דיגיטלי"},
  {prompt: "שיער ארוך או שיער קצר?", a: "💇‍♀️ ארוך", b: "💇 קצר"},
  {prompt: "גינה או מרפסת?", a: "🌳 גינה", b: "🏠 מרפסת"},
  {prompt: "פופקורן או נאצ׳וס?", a: "🍿 פופקורן", b: "🌮 נאצ׳וס"},
  {prompt: "מהר או לאט?", a: "⚡ מהר", b: "🐌 לאט"},
  {prompt: "לייב או סטורי?", a: "📡 לייב", b: "📱 סטורי"},
  {prompt: "אוברסייז או צמוד?", a: "👕 אוברסייז", b: "👔 צמוד"},
  {prompt: "סניקרס או כפכפים?", a: "👟 סניקרס", b: "🩴 כפכפים"},
];

const EXTRA_DILEMMAS = [
  {a: "📱 בלי סמארטפון לשנה", b: "💻 בלי לפטופ לשנה"},
  {a: "👀 לדעת מי צופה בסטורי שלך", b: "🚫 לדעת מי חסם אותך"},
  {a: "✅ עם וי כחול", b: "👻 ברור שבלי"},
  {a: "🎬 להיות יוטיובר מצליח", b: "🎮 להיות גיימר מקצועי"},
  {a: "🐌 אינטרנט איטי תמיד", b: "🪫 סוללה תמיד ב-5%"},
  {a: "🗑️ למחוק את הטיקטוק", b: "🗑️ למחוק את האינסטגרם"},
  {a: "🤦 להיות ויראלי על פדיחה", b: "🥷 להישאר אנונימי לנצח"},
  {a: "❤️ 1,000 לייקים", b: "💬 10 תגובות מרגשות"},
  {a: "🌶️ רק אוכל חריף", b: "🍫 רק אוכל מתוק"},
  {a: "☕ בלי קפה", b: "🍫 בלי שוקולד"},
  {a: "👨‍🍳 לבשל", b: "🛵 להזמין"},
  {a: "🍟 צ׳יפס בלי קטשופ", b: "🍔 המבורגר בלי לחמנייה"},
  {a: "🍕 פיצה עם אננס", b: "🧆 חומוס עם פול"},
  {a: "💧 לשתות רק מים", b: "🧃 לשתות רק מיץ"},
  {a: "🍦 גלידה בחורף", b: "🍲 מרק בקיץ"},
  {a: "🏖️ חופשת בטן-גב", b: "🎒 טיול תרמילאים"},
  {a: "⏰ לקום ב-5 בבוקר", b: "🌃 ללכת לישון ב-3 בלילה"},
  {a: "📚 ספר מודפס", b: "📱 קינדל / אונליין"},
  {a: "🏨 לחיות בבית מלון", b: "🏡 לחיות בוילה מבודדת"},
  {a: "🎤 לשיר בקריוקי", b: "🕺 לרקוד בהופעה"},
  {a: "🗣️ להגיד כל מה שאתה חושב", b: "🤐 לא לדבר בכלל"},
  {a: "⏪ לשנות את העבר", b: "⏩ לראות את העתיד"},
  {a: "😂 להיות מצחיק", b: "😍 להיות יפה"},
  {a: "💪 להיות חסין לכאב", b: "🛡️ להיות חסין לעלבונות"},
  {a: "👶 גיל 5", b: "👴 גיל 50"},
  {a: "👫 חבר טוב", b: "👥 100 מכרים"},
  {a: "😤 האמת הכואבת", b: "🤥 שישקרו"},
  {a: "💘 דייט עם מודל", b: "🧠 דייט עם גאון/ית"},
  {a: "🏠 לגור ליד ההורים", b: "🌍 לגור ביבשת אחרת"},
  {a: "👂 להקשיב", b: "🗣️ לדבר"},
  {a: "🏃 לרוץ ב-100 קמ״ש", b: "🕊️ לעוף ב-10 קמ״ש"},
  {a: "🐟 לנשום מתחת למים", b: "🐕 לדבר עם חיות"},
  {a: "🥵 תמיד חם לך", b: "🥶 תמיד קר לך"},
  {a: "🧦 גרביים רטובים", b: "👕 בגדים קטנים במידה"},
  {a: "🌈 שיער צבעוני", b: "🧑‍🦲 בלי שיער בכלל"},
  {a: "🦸 להיות גיבור על", b: "🦹 להיות נבל על"},
  {a: "💕 למצוא אהבת אמת", b: "💰 למצוא מזוודה עם מיליון"},
  {a: "👻 להיות בלתי נראה", b: "🧠 לקרוא מחשבות"},
  {a: "🧹 דירה נקייה תמיד", b: "🍔 מקרר מלא תמיד"},
  {a: "🛏️ מיטה נוחה", b: "🚿 מקלחת מפנקת"},
  {a: "🏝️ לחיות על אי בודד", b: "🗽 לחיות בניו יורק"},
  {a: "📺 טלוויזיה ענקית", b: "📚 ספרייה ענקית"},
  {a: "⛵ לגור בסירה", b: "🚐 לגור בקרוואן"},
  {a: "☕ שוקו חם", b: "🧊 אייס קפה"},
  {a: "🥜 במבה", b: "🌀 ביסלי"},
  {a: "🚿 מקלחת בבוקר", b: "🛁 מקלחת בערב"},
  {a: "🔄 לצפות בסרט שוב", b: "🆕 לראות סרט חדש"},
  {a: "🕯️ אור דלוק", b: "🌑 חושך מוחלט"},
  {a: "🎵 הופעה חיה", b: "🎬 סרט בקולנוע"},
  {a: "🎸 מוזיקת רוק", b: "🎤 מוזיקת פופ"},
  {a: "🎙️ פודקאסט", b: "📻 רדיו"},
  {a: "🎡 לונה פארק", b: "🖼️ מוזיאון"},
  {a: "😂 סטנדאפ", b: "🎭 הצגת תיאטרון"},
  {a: "📝 לכתוב ספר", b: "🎬 לביים סרט"},
  {a: "👀 להיות בקהל", b: "🎤 להיות על הבמה"},
  {a: "🌅 לראות זריחה", b: "🌇 לראות שקיעה"},
  {a: "🐴 סוס", b: "🐬 דולפין"},
  {a: "🌧️ גשם", b: "❄️ שלג"},
  {a: "⛺ לישון באוהל", b: "🏡 לישון בצימר"},
  {a: "⚽ כדורגל", b: "🏀 כדורסל"},
  {a: "🏄 גלישת גלים", b: "⛷️ סקי בשלג"},
  {a: "🧘 מדיטציה", b: "💃 ריקוד אינטנסיבי"},
  {a: "🤗 חיבוק", b: "💬 מילה טובה"},
  {a: "🌧️ יום גשום במיטה", b: "☀️ יום שטוף שמש בחוץ"},
  {a: "🎁 לקבל מתנה בהפתעה", b: "🛍️ לבחור את המתנה שלך"},
  {a: "🛒 קניות אונליין", b: "🏬 קניות בחנות"},
  {a: "💡 להתחיל מחדש", b: "➡️ להמשיך מאיפה שהפסקת"},
  {a: "🎲 לקחת סיכון", b: "🛡️ ללכת על בטוח"},
  {a: "🆓 להיות חופשי", b: "🔒 להיות בטוח"},
  {a: "🚀 לטייל בחלל", b: "🐙 לטייל במצולות הים"},
  {a: "🌐 להבין כל שפה בעולם", b: "🐾 לדעת מה חיות חושבות"},
  {a: "🤖 רובוט שינקה את הבית", b: "👨‍🍳 רובוט שיבשל"},
  {a: "🌙 תיירות לירח", b: "🐠 מלון מתחת למים"},
  {a: "🧠 לזכור כל מה שקראת", b: "🧮 לחשב חישובים בשנייה"},
  {a: "🎓 ציונים גבוהים", b: "💼 ניסיון מעשי"},
  {a: "📖 ללמוד בבוקר", b: "🌙 ללמוד בלילה"},
  {a: "👨‍🏫 מורה פרטי", b: "🌐 אינטרנט"},
  {a: "🤣 לדבר רק בחרוזים", b: "🤫 לדבר רק בלחישות"},
  {a: "⏰ תמיד להגיע שעה מוקדם", b: "😅 תמיד לאחר ב-10 דקות"},
  {a: "🟡 ללבוש חלק", b: "🔲 ללבוש משובץ"},
  {a: "🤢 להריח כמו בצל תמיד", b: "🐟 להריח כמו דג תמיד"},
  {a: "🪽 כנפיים של ציפור", b: "🐒 זנב של קוף"},
  {a: "😱 לחיות בסרט אימה", b: "🧸 לחיות בסרט מצויר"},
  {a: "🎉 הפתעה טובה", b: "📋 תכנון מוקפד"},
  {a: "💔 לסלוח", b: "🧠 לשכוח"},
  {a: "⭐ להיות מוערך", b: "💡 להיות מובן"},
  {a: "✈️ טיסה במחלקת עסקים", b: "🏨 מלון 5 כוכבים"},
  {a: "🚂 רכבת", b: "🚌 אוטובוס"},
  {a: "🗺️ לטייל בלי מפה", b: "🧭 לטייל עם מדריך"},
  {a: "🌏 אירופה", b: "🌏 המזרח הרחוק"},
  {a: "🛴 קורקינט חשמלי", b: "🏍️ אופנוע"},
  {a: "👑 להיות הבוס של עצמך", b: "🌟 להיות העובד הכי טוב"},
  ...BULK_DILEMMAS,
];


const ROUND_MS_DEFAULT = 5000;
const ROUND_MS_SLOW = 7000;

function normalizeRoundMs(n) {
  const v = Number(n);
  if (v === ROUND_MS_SLOW) return ROUND_MS_SLOW;
  return ROUND_MS_DEFAULT;
}

/** רוחב מקסימלי אחיד לכפתורים ומלבני תוכן במסכי דילמה */
const CONTENT_MAX_PX = 380;
const LEADERBOARD_REF = "tapOrSkip_leaderboard";
const CHALLENGE_FEED_REF = "tapOrSkip_challenge_feed";

function photoUrlForFeed(p) {
  if (typeof p !== "string" || !p) return "";
  const t = p.trim();
  if (t.length > 800) return "";
  if (t.startsWith("http://") || t.startsWith("https://")) return t;
  return "";
}

/** אחוז התאמה — רק שאלות שבהן שני הצדדים ענו (A או B) */
function challengeMatchPct(cmp) {
  const d = cmp?.answeredTotal ?? 0;
  return d ? Math.round((cmp.matches / d) * 100) : 0;
}

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
function vibrate(ms) { try { navigator?.vibrate?.(ms); } catch {} }
function makeResult() {
  const aPct = Math.floor(30 + Math.random() * 41);
  return {aPct, bPct: 100 - aPct};
}
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** סיווג גס לערבוב — מזון, טכנולוגיה, בילוי/תרבות, אחר */
const DILEMMA_FOOD_MARKERS = [
  "🍕", "🍣", "🍔", "🌮", "🍿", "☕", "🍵", "🥐", "🥯", "🧀", "🍝", "🍜", "🍩", "🥩", "🌯", "🍱", "🍛", "🧋", "🫖", "🍯", "🥜", "🍦", "🍲", "🥗", "🧇", "🥞", "🍪", "🧁", "🎂", "🍰", "🍫", "🍎", "🍊", "🍋", "🍉", "🍇", "🥒", "🫒", "🍳", "🥣", "🧃", "🥤", "🍺", "🍷", "🥂", "🧊", "🥡", "🧆", "🥓", "🍖", "🍢", "🍤", "🍡", "🍧", "🍨", "🍼", "🥛", "🍶", "🍾", "🍸", "🍹", "🍻", "🥃", "🧉", "פיצה", "סושי", "קפה", "תה", "המבורגר", "טאקו", "גלידה", "חומוס", "פסטה", "אוכל", "מסעדה", "בישול", "משלוח", "ארוחת", "ממרח", "פופקורן", "במבה", "ביסלי", "נאצ", "סלט", "מרק", "שוקולד", "גבינה", "קרואסון", "בייגל", "וופל", "פנקייק", "דונאט", "סטייק", "בוריטו", "בנטו", "קארי", "שייק", "סמוזי", "צ׳אי", "אספרסו", "חלבון", "יין", "בירה", "שוקו", "אייס", "בופה", "פאסט", "קינוח", "גרניטה", "עוגה", "עוגיות", "חטיף", "מתוק", "חריף", "צ׳יפס", "נקניק",
];
const DILEMMA_LEISURE_MARKERS = [
  "🎮", "🎬", "📺", "🎵", "🎤", "🎭", "🎡", "🎪", "🏖", "🏔", "🏔️", "🌊", "🏝", "⚽", "🏀", "🏄", "⛷", "🎿", "🎸", "🎹", "🎯", "🎳", "📻", "🎙", "🕺", "💃", "🎊", "🎉", "🎲", "🃏", "♟", "🧩", "🏟", "🖼", "🎠", "🛝", "🏊", "🤸", "🥊", "🧘", "🏋", "🚴", "🛵", "✈", "🚢", "🗼", "🗽", "🏛", "🏜", "🌴", "❄", "🌺", "🎒", "🥾", "🛶", "⛵", "🌅", "🌇", "🌧", "☀", "טיקטוק", "אינסטגרם", "נטפליקס", "יוטיוב", "משחק", "סרט", "סדרה", "הופעה", "קולנוע", "בינג׳", "פודקאסט", "גיימינג", "מוזיקה", "קריוקי", "סטנדאפ", "תיאטרון", "ריקוד", "טיול", "חופשה", "חוף", "פארק", "מוזיאון", "לונה", "כדורגל", "כדורסל", "גלישה", "באולינג", "הצגה", "קהל", "במה", "זריחה", "שקיעה", "קומדיה", "מדע בדיוני", "מיסתורין", "פרק ", "לייב", "סטורי", "פיד ", "אצטדיון", "הופעה חיה", "בחורף", "בקיץ", "באוהל", "צימר", "קרוואן", "סירה", "מסיבה", "משחקים", "חו״ל", "טיול בארץ", "ים או", "או הרים", "לשיר", "לרקוד", "ספר או", "דרמה", "ראפ", "רוק",
];
const DILEMMA_TECH_MARKERS = [
  "📱", "💻", "🖥", "⌨", "🖱", "📲", "🔋", "📶", "🌐", "💾", "🔐", "🤖", "📧", "🎥", "💡", "🔔", "🔕", "📡", "🛰", "📷", "📸", "✨", "🪞", "👆", "ווידאו", "קולית", "סמארטפון", "לפטופ", "מחשב", "מסך", "אינטרנט", "ווי-פיי", "וי-פיי", "טאבלט", "סוללה", "טעינה", "מקלדת", "עכבר", "אוזניות", "רמקולים", "זום", "אימייל", "גיבוי", "סיסמה", "זיהוי", "רובוט", "מצלמה", "פילטר", "התראות", "דיגיטלי", "קינדל", "אונליין", "שרת", "אפליקציה", "ביטול רעש", "מכני", "שקטה", "גיימר", "יוטיובר", "נתונים", "חיבור",
];

function dilemmaTextBlob(d) {
  return `${d?.prompt || ""} ${d?.a || ""} ${d?.b || ""}`;
}

function inferDilemmaCategory(d) {
  const t = dilemmaTextBlob(d);
  for (const m of DILEMMA_FOOD_MARKERS) {
    if (t.includes(m)) return "food";
  }
  for (const m of DILEMMA_LEISURE_MARKERS) {
    if (t.includes(m)) return "leisure";
  }
  for (const m of DILEMMA_TECH_MARKERS) {
    if (t.includes(m)) return "tech";
  }
  return "other";
}

/** ערבוב כך שלא תהיה אותה קטגוריה (מזון/טכנולוגיה/בילוי) פעמיים ברצף — כשאפשר */
function shuffleSpreadCategories(dilemmas) {
  const cats = ["food", "tech", "leisure", "other"];
  const buckets = {food: [], tech: [], leisure: [], other: []};
  for (const d of dilemmas) {
    buckets[inferDilemmaCategory(d)].push(d);
  }
  for (const c of cats) {
    buckets[c] = shuffle(buckets[c]);
  }
  const out = [];
  let last = null;
  while (out.length < dilemmas.length) {
    const nonEmpty = cats.filter((c) => buckets[c].length > 0);
    if (nonEmpty.length === 0) break;
    const lastCat = last;
    const avoidRepeat = nonEmpty.filter((c) => c !== lastCat);
    const pickFrom = avoidRepeat.length ? avoidRepeat : nonEmpty;
    const pickCat = pickFrom[Math.floor(Math.random() * pickFrom.length)];
    out.push(buckets[pickCat].shift());
    last = pickCat;
  }
  return out;
}

function loadLocalProfile() {
  try {
    const raw = localStorage.getItem(LOCAL_PROFILE_KEY);
    if (!raw) return {name: "", photo: ""};
    const p = JSON.parse(raw);
    return {
      name: typeof p?.name === "string" ? p.name : "",
      photo: typeof p?.photo === "string" ? p.photo : "",
    };
  } catch {
    return {name: "", photo: ""};
  }
}

function saveLocalProfile(next) {
  try {
    localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function loadLocalSettings() {
  try {
    const raw = localStorage.getItem(LOCAL_SETTINGS_KEY);
    if (!raw) return {endless: false, roundMs: ROUND_MS_DEFAULT};
    const s = JSON.parse(raw);
    return {
      endless: typeof s?.endless === "boolean" ? s.endless : false,
      roundMs: normalizeRoundMs(s?.roundMs),
    };
  } catch {
    return {endless: false, roundMs: ROUND_MS_DEFAULT};
  }
}

function saveLocalSettings(partial) {
  try {
    const cur = loadLocalSettings();
    localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify({...cur, ...partial}));
  } catch {
    /* ignore */
  }
}

const stripEmoji = (s) => s.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]\uFE0F?\s*/gu, "").trim();

function normalizeDilemmas() {
  const base = DEFAULT_DILEMMAS.map((d) => ({prompt: d.prompt, a: d.a, b: d.b}));
  const extra = EXTRA_DILEMMAS.map((d) => ({
    prompt: `${stripEmoji(d.a)} או ${stripEmoji(d.b)}?`,
    a: d.a,
    b: d.b,
  }));
  return [...base, ...extra];
}

function ShareIconWa() {
  return (
    <svg viewBox="0 0 32 32" width={30} height={30} fill="currentColor" aria-hidden>
      <path d="M16.004 0C7.164 0 .002 7.158.002 15.995c0 2.82.737 5.573 2.14 7.998L0 32l8.247-2.16a15.95 15.95 0 007.753 1.977h.004C24.84 31.817 32 24.66 32 15.995 32 7.158 24.844 0 16.004 0zm0 29.313a13.28 13.28 0 01-6.97-1.97l-.5-.297-5.19 1.36 1.384-5.06-.326-.518a13.22 13.22 0 01-2.028-7.05c0-7.34 5.98-13.312 13.326-13.312 7.345 0 13.326 5.972 13.326 13.312 0 7.34-5.98 13.536-13.022 13.536zm7.304-9.975c-.4-.2-2.367-1.168-2.734-1.3-.367-.134-.634-.2-.9.2-.268.4-1.034 1.3-1.268 1.568-.234.267-.467.3-.867.1-.4-.2-1.69-.623-3.22-1.987-1.19-1.062-1.993-2.374-2.227-2.774-.234-.4-.025-.616.176-.815.18-.18.4-.467.6-.7.2-.234.267-.4.4-.668.134-.267.067-.5-.033-.7-.1-.2-.9-2.168-1.234-2.968-.325-.78-.656-.674-.9-.686l-.767-.013c-.267 0-.7.1-1.067.5-.367.4-1.4 1.368-1.4 3.336 0 1.968 1.434 3.87 1.634 4.136.2.267 2.822 4.31 6.838 6.042.955.413 1.7.659 2.282.844.96.305 1.832.262 2.522.159.77-.115 2.367-.968 2.7-1.902.334-.935.334-1.735.234-1.902-.1-.168-.367-.267-.767-.467z" />
    </svg>
  );
}

function ShareIconTt() {
  return (
    <svg viewBox="0 0 24 24" width={28} height={28} fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
    </svg>
  );
}

function ShareIconIg() {
  return (
    <svg viewBox="0 0 24 24" width={28} height={28} fill="currentColor" aria-hidden>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.97.24 2.43.403a4.08 4.08 0 011.47.958c.453.453.78.898.957 1.47.164.46.35 1.26.404 2.43.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.24 1.97-.404 2.43a4.08 4.08 0 01-.957 1.47 4.08 4.08 0 01-1.47.957c-.46.164-1.26.35-2.43.404-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.97-.24-2.43-.404a4.08 4.08 0 01-1.47-.957 4.08 4.08 0 01-.957-1.47c-.164-.46-.35-1.26-.404-2.43C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.054-1.17.24-1.97.404-2.43a4.08 4.08 0 01.957-1.47A4.08 4.08 0 015.064 2.293c.46-.164 1.26-.35 2.43-.404C8.76 1.831 9.14 1.819 12 1.819zm0-1.656C8.741.163 8.333.175 7.053.234 5.775.293 4.905.493 4.14.793a5.73 5.73 0 00-2.08 1.353A5.73 5.73 0 00.708 4.226c-.3.765-.5 1.635-.56 2.913C.09 8.42.078 8.828.078 12.087s.012 3.668.07 4.948c.06 1.278.26 2.148.56 2.913a5.73 5.73 0 001.352 2.08 5.73 5.73 0 002.08 1.352c.765.3 1.635.5 2.913.56 1.28.058 1.688.07 4.947.07s3.668-.012 4.948-.07c1.278-.06 2.148-.26 2.913-.56a5.73 5.73 0 002.08-1.352 5.73 5.73 0 001.352-2.08c.3-.765.5-1.635.56-2.913.058-1.28.07-1.688.07-4.948s-.012-3.667-.07-4.947c-.06-1.278-.26-2.148-.56-2.913a5.73 5.73 0 00-1.352-2.08A5.73 5.73 0 0019.862.794c-.765-.3-1.635-.5-2.913-.56C15.668.175 15.26.163 12 .163zM12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z" />
    </svg>
  );
}

function ShareIconLink() {
  return (
    <svg viewBox="0 0 24 24" width={28} height={28} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

function FooterMailIcon() {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

const STARS = Array.from({length: 60}, (_, i) => {
  const angle = Math.random() * 360;
  const speed = 3 + Math.random() * 8;
  const size = Math.random() < 0.15 ? 2.5 + Math.random() * 1.5 : 0.8 + Math.random() * 1.5;
  const delay = Math.random() * speed;
  return { id: i, angle, speed, size, delay };
});

export const TapOrSkip = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const challengeIdFromUrl = searchParams.get("challenge") ?? "";
  const [screen, setScreen] = useState("menu");
  const [countdownNum, setCountdownNum] = useState(3);

  const [dilemmaList, setDilemmaList] = useState([]);
  const [endless, setEndless] = useState(() => loadLocalSettings().endless);
  const [roundMs, setRoundMs] = useState(() => loadLocalSettings().roundMs);
  const [sessionMode, setSessionMode] = useState("normal");
  const [challengeProfile, setChallengeProfile] = useState(() => loadLocalProfile());
  const [friendProfile, setFriendProfile] = useState(() => loadLocalProfile());
  const [friendChallengeId, setFriendChallengeId] = useState(null);
  const [loadedChallengeMeta, setLoadedChallengeMeta] = useState(null);
  const [createdChallengeId, setCreatedChallengeId] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [showMyChallenges, setShowMyChallenges] = useState(false);
  const [myChallengesRows, setMyChallengesRows] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [lastRunStats, setLastRunStats] = useState(null);

  const roundLogRef = useRef([]);
  const roundQuestionStartRef = useRef(0);

  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("question");
  const [choice, setChoice] = useState(null);
  const [result, setResult] = useState(() => makeResult());
  const [answered, setAnswered] = useState(0);
  const [majorityMatches, setMajorityMatches] = useState(0);
  const [msLeft, setMsLeft] = useState(() => loadLocalSettings().roundMs);
  const [animState, setAnimState] = useState("idle");

  const [showHowTo, setShowHowTo] = useState(false);
  const [showMenuSettings, setShowMenuSettings] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(null);
  const [challengeEndedPopup, setChallengeEndedPopup] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimer = useRef(null);
  const [challengeFeedData, setChallengeFeedData] = useState([]);
  const [playerName, setPlayerName] = useState(() => loadLocalProfile().name || "");
  const [playerPhoto, setPlayerPhoto] = useState(() => loadLocalProfile().photo || null);
  const [submitted, setSubmitted] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [menuLogoReady, setMenuLogoReady] = useState(false);

  const rafRef = useRef(null);
  const roundStartRef = useRef(0);
  const transitioningRef = useRef(false);

  const pool = useMemo(() => shuffleSpreadCategories(normalizeDilemmas()), []);
  const dilemma = endless && sessionMode === "normal"
    ? (pool[index % pool.length] || {prompt: "", a: "", b: ""})
    : (dilemmaList[index] || {prompt: "", a: "", b: ""});
  const nDilemmas = endless && sessionMode === "normal" ? Infinity : dilemmaList.length;
  const majority = result.aPct >= 50 ? "A" : "B";
  const similarityPct = answered === 0 ? 0 : Math.round((majorityMatches / answered) * 100);

  const effectiveRoundMs = useMemo(() => {
    if (sessionMode === "challenge_friend" && loadedChallengeMeta != null) {
      return normalizeRoundMs(loadedChallengeMeta.roundMs);
    }
    return normalizeRoundMs(roundMs);
  }, [sessionMode, loadedChallengeMeta, roundMs]);

  const progress = nDilemmas ? msLeft / effectiveRoundMs : 0;

  const sessionModeRef = useRef(sessionMode);
  sessionModeRef.current = sessionMode;

  useEffect(() => {
    const prev = document.title;
    document.title = "דילמה";
    return () => {
      document.title = prev;
    };
  }, []);

  /** זוגות אחרונים לפי זמן סיום תשובת החבר (כולל אתגרים ישנים), לא רק tapOrSkip_challenge_feed */
  useEffect(() => {
    let cancelled = false;
    let loadGen = 0;
    const q = query(ref(db, CHALLENGE_FRIEND_REF), orderByChild("ts"), limitToLast(30));
    const unsub = onValue(
      q,
      (snap) => {
        const gen = ++loadGen;
        const rows = [];
        snap.forEach((child) => {
          const v = child.val();
          if (v && typeof v.ts === "number") {
            rows.push({id: child.key, friend: v, ts: v.ts});
          }
        });
        rows.sort((a, b) => b.ts - a.ts);
        Promise.all(
          rows.map(async (row) => {
            const metaSnap = await get(ref(db, `${CHALLENGE_META_REF}/${row.id}`));
            const meta = metaSnap.val();
            if (!meta) return null;
            const fn = typeof row.friend.name === "string" ? row.friend.name.trim() : "";
            const cmp = compareChallengeRuns(meta.creatorRounds, row.friend.rounds, meta.dilemmas);
            const matchPct = challengeMatchPct(cmp);
            const bothPick = cmp.sames?.[0]?.choice;
            const bothLine = bothPick ? String(bothPick).trim().slice(0, 120) : "";
            return {
              id: row.id,
              ts: row.ts,
              creatorName: meta.creatorName || "—",
              friendName: fn || "—",
              creatorPhoto: photoUrlForFeed(meta.creatorPhoto),
              friendPhoto: photoUrlForFeed(row.friend.photo),
              matchPct,
              bothLine,
            };
          })
        )
          .then((built) => {
            if (cancelled || gen !== loadGen) return;
            setChallengeFeedData(built.filter(Boolean));
          })
          .catch((e) => {
            console.error("challenge pairs feed", e);
            if (!cancelled && gen === loadGen) setChallengeFeedData([]);
          });
      },
      (err) => {
        console.error("challenge pairs feed listener", err);
        setChallengeFeedData([]);
      }
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      if (user) {
        mergeAnonMyChallengesIntoUid(user.uid);
        const name = user.displayName || user.email?.split("@")[0] || "";
        if (name) setPlayerName(name.slice(0, 50));
        if (user.photoURL) setPlayerPhoto(user.photoURL);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    const name = (playerName || "").trim();
    const photo = typeof playerPhoto === "string" ? playerPhoto : "";
    if (!name && !photo) return;
    saveLocalProfile({name, photo});
  }, [playerName, playerPhoto]);

  useEffect(() => {
    saveLocalSettings({endless});
  }, [endless]);

  useEffect(() => {
    saveLocalSettings({roundMs: normalizeRoundMs(roundMs)});
  }, [roundMs]);

  useEffect(() => {
    if (screen !== "menu") setShowMenuSettings(false);
  }, [screen]);

  useEffect(() => {
    const img = new Image();
    const done = () => setMenuLogoReady(true);
    img.onload = done;
    img.onerror = done;
    img.src = DILEMA_LOGO_URL;
    if (img.complete) done();
  }, []);

  const signInGoogle = async () => {
    setAuthBusy(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      console.error(e);
      alert("ההתחברות נכשלה. ודאו ש-Google מופעל ב-Firebase Console (Authentication).");
    } finally {
      setAuthBusy(false);
    }
  };

  const signOutGoogle = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!challengeIdFromUrl) return;
    setFriendChallengeId(challengeIdFromUrl);
    setScreen("loading");
    get(ref(db, `${CHALLENGE_META_REF}/${challengeIdFromUrl}`))
      .then((snap) => {
        if (!snap.exists()) {
          alert("האתגר לא נמצא");
          setSearchParams({}, {replace: true});
          setScreen("menu");
          return;
        }
        return get(ref(db, `${CHALLENGE_FRIEND_REF}/${challengeIdFromUrl}`)).then((fSnap) => {
          if (fSnap.exists()) {
            setChallengeEndedPopup({meta: snap.val(), friend: fSnap.val()});
            setSearchParams({}, {replace: true});
            setFriendChallengeId(null);
            setScreen("menu");
            return;
          }
          setLoadedChallengeMeta(snap.val());
          setScreen("friend_intro");
        });
      })
      .catch(() => {
        alert("שגיאה בטעינת האתגר");
        setSearchParams({}, {replace: true});
        setScreen("menu");
      });
  }, [challengeIdFromUrl, setSearchParams]);

  const runCountdownToGame = useCallback((durationMs) => {
    const rm = normalizeRoundMs(durationMs);
    setScreen("countdown");
    setCountdownNum(3);
    let n = 3;
    const iv = setInterval(() => {
      n--;
      if (n > 0) setCountdownNum(n);
      else {
        clearInterval(iv);
        setCountdownNum(0);
        setTimeout(() => {
          setIndex(0);
          setPhase("question");
          setChoice(null);
          setResult(makeResult());
          setAnswered(0);
          setMajorityMatches(0);
          setMsLeft(rm);
          setAnimState("idle");
          setSubmitted(false);
          setScreen("game");
        }, 600);
      }
    }, 800);
  }, []);

  const startNormalGame = useCallback(() => {
    setSessionMode("normal");
    if (endless) {
      // Endless feels endless: no visible total count, questions repeat in a shuffled pool.
      setDilemmaList([]);
    } else {
      // Finite mode: take 15 from the full pool (defaults + extras).
      setDilemmaList(shuffleSpreadCategories(normalizeDilemmas()).slice(0, 15));
    }
    roundLogRef.current = [];
    setLastRunStats(null);
    setCompareResult(null);
    runCountdownToGame(roundMs);
  }, [endless, runCountdownToGame, roundMs]);

  const beginChallengeCreatorPlay = useCallback((overrideName) => {
    const name = overrideName || challengeProfile.name;
    if (!name?.trim()) {
      alert("נא להזין שם");
      return;
    }
    setSessionMode("challenge_create");
    setDilemmaList(shuffleSpreadCategories(normalizeDilemmas()).slice(0, 15));
    roundLogRef.current = [];
    setLastRunStats(null);
    runCountdownToGame(roundMs);
  }, [challengeProfile.name, runCountdownToGame, roundMs]);

  const beginFriendPlay = useCallback(() => {
    const user = auth.currentUser;
    const name = user?.displayName || user?.email?.split("@")[0] || "אנונימי";
    const photo = user?.photoURL || "";
    setFriendProfile({name, photo});
    const list = loadedChallengeMeta?.dilemmas;
    if (!list?.length) return;
    setSessionMode("challenge_friend");
    setDilemmaList(list);
    roundLogRef.current = [];
    setLastRunStats(null);
    runCountdownToGame(normalizeRoundMs(loadedChallengeMeta?.roundMs));
  }, [loadedChallengeMeta, runCountdownToGame]);

  const saveChallengeAndShare = async (stats) => {
    try {
      const newRef = push(ref(db, CHALLENGE_META_REF));
      const id = newRef.key;
      await set(newRef, {
        creatorName: challengeProfile.name.trim(),
        creatorPhoto: challengeProfile.photo || "",
        dilemmas: dilemmaList,
        creatorRounds: stats.rounds,
        creatorSimilarity: stats.similarityPct,
        creatorAvgMs: stats.avgMs,
        roundMs: stats.roundMs ?? normalizeRoundMs(roundMs),
        createdAt: Date.now(),
      });
      addMyChallengeId(id, "creator", auth.currentUser?.uid);
      setCreatedChallengeId(id);
      setScreen("challenge_share");
    } catch (e) {
      console.error(e);
      alert("שמירת האתגר נכשלה");
      setScreen("gameover");
    }
  };

  const saveFriendAnswer = async (stats) => {
    try {
      const friendUid = auth.currentUser?.uid || "";
      await set(ref(db, `${CHALLENGE_FRIEND_REF}/${friendChallengeId}`), {
        name: friendProfile.name.trim(),
        photo: friendProfile.photo || "",
        rounds: stats.rounds,
        similarityPct: stats.similarityPct,
        avgMs: stats.avgMs,
        ts: Date.now(),
        ...(friendUid ? {googleUid: friendUid} : {}),
      });
      if (friendChallengeId) addMyChallengeId(friendChallengeId, "friend", friendUid || undefined);
      const cmp = compareChallengeRuns(
        loadedChallengeMeta.creatorRounds,
        stats.rounds,
        loadedChallengeMeta.dilemmas
      );
      const matchPctFeed = challengeMatchPct(cmp);
      const bothPick = cmp.sames?.[0]?.choice;
      const bothLineFeed = bothPick ? String(bothPick).trim().slice(0, 120) : "";
      if (friendUid) {
        try {
          await push(ref(db, CHALLENGE_FEED_REF), {
            ts: Date.now(),
            googleUid: friendUid,
            creatorName: (loadedChallengeMeta.creatorName || "").slice(0, 50),
            friendName: friendProfile.name.trim().slice(0, 50),
            creatorPhoto: photoUrlForFeed(loadedChallengeMeta.creatorPhoto),
            friendPhoto: photoUrlForFeed(friendProfile.photo),
            matchPct: matchPctFeed,
            matchCount: cmp.matches,
            totalQs: cmp.answeredTotal,
            bothLine: bothLineFeed.slice(0, 220),
            avgMs: stats.avgMs,
          });
        } catch (feedErr) {
          console.error(feedErr);
        }
      }
      setCompareResult(cmp);
      setScreen("challenge_compare");
    } catch (e) {
      console.error(e);
      alert("שמירת התוצאה נכשלה");
      setScreen("gameover");
    }
  };

  const resolveGameEnd = (stats) => {
    setLastRunStats(stats);
    const mode = sessionModeRef.current;
    if (mode === "challenge_create") {
      saveChallengeAndShare(stats);
    } else if (mode === "challenge_friend") {
      saveFriendAnswer(stats);
    } else {
      if (auth.currentUser && !submitted) {
        autoSubmitScore(stats);
      }
      setScreen("gameover");
    }
  };

  const openMyChallengesList = async () => {
    const byId = new Map();
    const user = auth.currentUser;
    for (const x of loadMyChallengeIds(user?.uid)) {
      if (!x?.id) continue;
      byId.set(x.id, {
        id: x.id,
        role: x.role === "friend" ? "friend" : "creator",
        at: typeof x.at === "number" ? x.at : 0,
      });
    }
    if (user?.uid) {
      try {
        const uidQ = query(ref(db, CHALLENGE_FRIEND_REF), orderByChild("googleUid"), equalTo(user.uid));
        const uidSnap = await get(uidQ);
        uidSnap.forEach((child) => {
          const id = child.key;
          const v = child.val();
          const ts = typeof v?.ts === "number" ? v.ts : 0;
          const prev = byId.get(id);
          if (!prev) {
            byId.set(id, {id, role: "friend", at: ts});
          } else if (prev.role !== "creator") {
            byId.set(id, {id, role: "friend", at: Math.max(prev.at, ts)});
          }
        });
      } catch (e) {
        console.error("my challenges by googleUid", e);
      }
    }
    const sorted = [...byId.values()].sort((a, b) => b.at - a.at);
    const rows = await Promise.all(
      sorted.map(async ({id, role}) => {
        const [metaSnap, friendSnap] = await Promise.all([
          get(ref(db, `${CHALLENGE_META_REF}/${id}`)),
          get(ref(db, `${CHALLENGE_FRIEND_REF}/${id}`)),
        ]);
        return {id, role, meta: metaSnap.val(), friend: friendSnap.val() || null};
      })
    );
    setMyChallengesRows(rows.filter((r) => r.meta));
    setShowMyChallenges(true);
  };

  const startRound = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    roundStartRef.current = performance.now();
    setMsLeft(effectiveRoundMs);
    const tick = () => {
      const elapsed = performance.now() - roundStartRef.current;
      const left = clamp(effectiveRoundMs - elapsed, 0, effectiveRoundMs);
      setMsLeft(left);
      if (left <= 0) return;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [effectiveRoundMs]);

  useEffect(() => {
    if (screen === "game" && phase === "question") {
      roundQuestionStartRef.current = performance.now();
    }
  }, [screen, phase, index]);

  useEffect(() => {
    if (screen !== "game" || phase !== "question") return;
    startRound();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [index, phase, screen, startRound]);

  useEffect(() => {
    if (screen !== "game" || phase !== "question") return;
    if (msLeft <= 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      roundLogRef.current.push({
        i: index,
        choice: null,
        msUsed: effectiveRoundMs,
        majority: result.aPct >= 50 ? "A" : "B",
      });
      setChoice(null);
      setPhase("results");
    }
  }, [msLeft, screen, phase, index, result, effectiveRoundMs]);

  const onPick = (picked) => {
    if (phase !== "question") return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    vibrate(15);
    roundLogRef.current.push({
      i: index,
      choice: picked,
      msUsed: Math.round(performance.now() - roundQuestionStartRef.current),
      majority: result.aPct >= 50 ? "A" : "B",
    });
    setChoice(picked);
    setPhase("results");
    setAnswered((v) => v + 1);
    if (picked === majority) setMajorityMatches((v) => v + 1);
  };

  const next = () => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    vibrate(8);
    setAnimState("exit");
    setTimeout(() => {
      const nextIdx = index + 1;
      if (nextIdx >= nDilemmas) {
        const stats = {
          similarityPct,
          avgMs: avgMsFromRounds(roundLogRef.current),
          answered,
          matchCount: majorityMatches,
          rounds: [...roundLogRef.current],
          roundMs: effectiveRoundMs,
        };
        resolveGameEnd(stats);
        transitioningRef.current = false;
        return;
      }
      setIndex(nextIdx);
      setPhase("question");
      setChoice(null);
      setResult(makeResult());
      setMsLeft(effectiveRoundMs);
      setAnimState("enter");
      setTimeout(() => { setAnimState("idle"); transitioningRef.current = false; }, 220);
    }, 180);
  };

  const finishEarly = () => {
    if (transitioningRef.current) return;
    transitioningRef.current = false;
    vibrate(8);
    const stats = {
      similarityPct,
      avgMs: avgMsFromRounds(roundLogRef.current),
      answered,
      matchCount: majorityMatches,
      rounds: [...roundLogRef.current],
      roundMs: effectiveRoundMs,
    };
    resolveGameEnd(stats);
  };

  const autoSubmitScore = (stats) => {
    const user = auth.currentUser;
    if (!user || submitted) return;
    const name = user.displayName || user.email?.split("@")[0] || "אנונימי";
    const photo = user.photoURL || null;
    const avgMs = stats?.avgMs ?? avgMsFromRounds(roundLogRef.current);
    const rounds = stats?.rounds ?? roundLogRef.current;
    const minsRaw = rounds
      .filter(r => r.choice && r.choice !== r.majority)
      .map(r => {
        const d = dilemmaList[r.i];
        if (!d) return null;
        return {yourPick: r.choice === "A" ? d.a : d.b, otherPick: r.choice === "A" ? d.b : d.a};
      })
      .filter(Boolean);
    const insight = minsRaw.length > 0
      ? `מעדיף ${minsRaw[0].yourPick} על ${minsRaw[0].otherPick}`
      : `${stats?.similarityPct ?? similarityPct}% דמיון לרוב`;
    push(ref(db, LEADERBOARD_REF), {
      name: name.slice(0, 50),
      score: stats?.similarityPct ?? similarityPct,
      answered: stats?.answered ?? answered,
      photo,
      ts: Date.now(),
      avgMs,
      matchCount: stats?.matchCount ?? majorityMatches,
      insight,
      googleUid: user.uid,
    });
    setSubmitted(true);
  };

  const handleChallengePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 150000) { alert("הקובץ גדול מדי (מקסימום 150KB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setChallengeProfile((p) => ({...p, photo: ev.target.result}));
    reader.readAsDataURL(file);
  };

  const handleFriendPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 150000) { alert("הקובץ גדול מדי (מקסימום 150KB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setFriendProfile((p) => ({...p, photo: ev.target.result}));
    reader.readAsDataURL(file);
  };

  const goToMenu = useCallback(() => {
    setSearchParams({}, {replace: true});
    setFriendChallengeId(null);
    setLoadedChallengeMeta(null);
    setCreatedChallengeId(null);
    setCompareResult(null);
    setDilemmaList([]);
    setScreen("menu");
  }, [setSearchParams]);

  const showToast = useCallback((msg) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMsg(msg);
    toastTimer.current = setTimeout(() => setToastMsg(null), 2400);
  }, []);

  const shareLink = createdChallengeId ? buildChallengeLink(createdChallengeId) : "";

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(() => showToast("הקישור הועתק! ✓")).catch(() => showToast(shareLink));
  };

  const shareWhatsApp = () => {
    if (!shareLink) return;
    const t = encodeURIComponent(`אתגר אותי בדילמה! 🎯\n${shareLink}`);
    window.open(`https://wa.me/?text=${t}`, "_blank", "noopener,noreferrer");
  };

  const shareTikTokHint = () => {
    if (!shareLink) return;
    const cap = `אתגר אותי בדילמה! ${shareLink}\n#דילמה #אתגר`;
    navigator.clipboard.writeText(cap).then(() => showToast("ניסוח להעלאה הועתק — הדביקו בטיקטוק / סטורי ✓")).catch(() => showToast(cap));
  };

  const shareInstagram = () => {
    if (!shareLink) return;
    const cap = `אתגר אותי בדילמה! 🎯\n${shareLink}\n#דילמה #אתגר`;
    navigator.clipboard.writeText(cap).then(() => showToast("הטקסט הועתק — הדביקו באינסטגרם ✓")).catch(() => showToast(cap));
  };

  const createChallengeFromLastRun = async () => {
    const playedDilemmas =
      dilemmaList.length > 0
        ? dilemmaList
        : lastRunStats?.rounds?.length
          ? lastRunStats.rounds.map((r) => pool[r.i % pool.length])
          : [];
    if (!lastRunStats || !playedDilemmas.length) {
      showToast("לא ניתן ליצור אתגר — אין מספיק נתונים מהסיבוב");
      return;
    }
    const user = auth.currentUser;
    const name = user?.displayName || user?.email?.split("@")[0] || playerName || "אנונימי";
    const photo = user?.photoURL || "";
    try {
      const newRef = push(ref(db, CHALLENGE_META_REF));
      const id = newRef.key;
      await set(newRef, {
        creatorName: name.trim(),
        creatorPhoto: photo,
        dilemmas: playedDilemmas,
        creatorRounds: lastRunStats.rounds,
        creatorSimilarity: lastRunStats.similarityPct,
        creatorAvgMs: lastRunStats.avgMs,
        roundMs: lastRunStats.roundMs ?? normalizeRoundMs(roundMs),
        createdAt: Date.now(),
      });
      addMyChallengeId(id, "creator", user?.uid);
      setCreatedChallengeId(id);
      setScreen("challenge_share");
    } catch (e) {
      console.error(e);
      alert("שמירת האתגר נכשלה");
    }
  };

  const personalityInsights = useMemo(() => {
    if (!lastRunStats) return null;
    const sim = lastRunStats.similarityPct ?? 0;
    const avgMs = lastRunStats.avgMs ?? 0;
    const rounds = lastRunStats.rounds ?? [];

    let headline = "";
    let emoji = "";
    if (sim >= 85) { headline = "אתה ממש כמו כולם, יותר קרינג׳ מזה אין 😬"; emoji = "🐑"; }
    else if (sim >= 70) { headline = "דיי קרוב לכולם — את/ה הולך/ת עם הזרם"; emoji = "🌊"; }
    else if (sim >= 50) { headline = "חצי-חצי — לפעמים עם כולם, לפעמים בדרך שלך"; emoji = "⚖️"; }
    else if (sim >= 35) { headline = "וואוו אתה כל כך מיוחד ושונה 💫"; emoji = "🦄"; }
    else { headline = "אתה מעולם אחר לגמרי — רובם בחרו הפוך ממך!"; emoji = "👽"; }

    const skipped = rounds.filter((r) => r.choice == null).length;
    const answeredRounds = rounds.filter((r) => r.choice != null);
    const avgMsAnswered =
      answeredRounds.length > 0
        ? Math.round(
            answeredRounds.reduce((s, r) => s + (r.msUsed || 0), 0) / answeredRounds.length
          )
        : 0;
    const avgSecAnswered =
      avgMsAnswered > 0 ? Math.round((avgMsAnswered / 1000) * 10) / 10 : 0;
    const secStrAnswered = avgSecAnswered.toFixed(1);
    const speedAvgLine =
      avgMsAnswered > 0 ? `זמן ממוצע ${secStrAnswered} שניות` : "";
    const speedSkippedLine =
      skipped > 0
        ? skipped === 1
          ? "פספסת שאלה אחת"
          : `פספסת ${skipped} שאלות`
        : "";

    const minorities = rounds
      .filter(r => r.choice && r.choice !== r.majority)
      .map(r => {
        const d = dilemmaList[r.i];
        if (!d) return null;
        const yourPick = r.choice === "A" ? d.a : d.b;
        const otherPick = r.choice === "A" ? d.b : d.a;
        return {prompt: d.prompt, yourPick, otherPick};
      })
      .filter(Boolean);

    return {headline, emoji, speedAvgLine, speedSkippedLine, minorities, sim, avgMs};
  }, [lastRunStats, dilemmaList]);

  return (
    <FullScreen dir="rtl" lang="he">
      <LiveBg>
        <Nebula />
        {STARS.map(s => (
          <Star key={s.id} $angle={s.angle} $speed={s.speed} $size={s.size} $delay={s.delay} />
        ))}
      </LiveBg>

      {screen === "loading" && (
        <CenterScreen>
          <MenuSubtitle>טוען אתגר…</MenuSubtitle>
        </CenterScreen>
      )}

      {screen === "challenge_setup" && (
        <CenterScreen>
          <WizardCard>
            <WizardTitle>אתגר חבר 🎯</WizardTitle>
            {authUser && (
              <FriendInvite>
                {authUser.photoURL ? <FriendMini src={authUser.photoURL} alt="" /> : <FriendEmoji>👤</FriendEmoji>}
                <span><strong>{authUser.displayName || authUser.email?.split("@")[0]}</strong></span>
              </FriendInvite>
            )}
            <WizardList>
              <WizardLi><strong>1.</strong> שחקו סיבוב מלא</WizardLi>
              <WizardLi><strong>2.</strong> שלחו לחבר ב־WhatsApp או טיקטוק</WizardLi>
              <WizardLi><strong>3.</strong> אחרי שיחקו — תראו השוואה מלאה</WizardLi>
            </WizardList>
            <WizardActions>
              <PlayBtn type="button" onClick={() => {
                const name = authUser?.displayName || authUser?.email?.split("@")[0] || "אנונימי";
                const photo = authUser?.photoURL || "";
                setChallengeProfile({name, photo});
                beginChallengeCreatorPlay(name);
              }}><Bubbles/>התחל משחק</PlayBtn>
              <GhostBtn type="button" onClick={() => setScreen("menu")}>ביטול</GhostBtn>
            </WizardActions>
          </WizardCard>
        </CenterScreen>
      )}

      {screen === "friend_intro" && loadedChallengeMeta && (
        <CenterScreen>
          <WizardCard>
            <WizardTitle>הוזמנת לאתגר! 🎯</WizardTitle>
            <FriendInvite>
              <span>
                {loadedChallengeMeta.creatorPhoto?.startsWith("http") || loadedChallengeMeta.creatorPhoto?.startsWith("data:") ? (
                  <FriendMini src={loadedChallengeMeta.creatorPhoto} alt="" />
                ) : (
                  <FriendEmoji>{loadedChallengeMeta.creatorPhoto || "🙂"}</FriendEmoji>
                )}
              </span>
              <span><strong>{loadedChallengeMeta.creatorName}</strong> אתגר אותך בדילמה</span>
            </FriendInvite>
            <WizardList>
              <WizardLi>אותן שאלות, אותו סדר — נשווה ביניכם בסוף</WizardLi>
            </WizardList>
            {authUser ? (
              <WizardActions>
                <PlayBtn type="button" onClick={beginFriendPlay}><Bubbles/>שחק עכשיו</PlayBtn>
                <GhostBtn type="button" onClick={goToMenu}>דלג</GhostBtn>
              </WizardActions>
            ) : (
              <WizardActions>
                <GoogleSignInBtn type="button" onClick={async () => {
                  await signInGoogle();
                }} disabled={authBusy}>
                  <GoogleG aria-hidden="true">G</GoogleG>
                  {authBusy ? "מתחבר…" : "התחבר עם Google כדי להמשיך"}
                </GoogleSignInBtn>
                <GhostBtn type="button" onClick={goToMenu}>דלג</GhostBtn>
              </WizardActions>
            )}
          </WizardCard>
        </CenterScreen>
      )}

      {screen === "challenge_share" && createdChallengeId && (
        <CenterScreen>
          <WizardCard>
            <WizardTitle>שלחו לחבר!</WizardTitle>
            <ShareHint>שלח לחבר/ה, הם ישחקו, מי יודע אולי תמצאו תחומי עניין.</ShareHint>
            <ShareLinkBox dir="ltr">{shareLink}</ShareLinkBox>
            <ShareDock>
              <ShareIconBtn type="button" onClick={shareWhatsApp} aria-label="WhatsApp" title="WhatsApp">
                <ShareIconWa />
              </ShareIconBtn>
              <ShareIconBtn type="button" onClick={shareInstagram} aria-label="Instagram" title="Instagram">
                <ShareIconIg />
              </ShareIconBtn>
              <ShareIconBtn type="button" onClick={shareTikTokHint} aria-label="TikTok" title="TikTok">
                <ShareIconTt />
              </ShareIconBtn>
              <ShareIconBtn type="button" onClick={copyShareLink} aria-label="העתק קישור" title="קישור">
                <ShareIconLink />
              </ShareIconBtn>
            </ShareDock>
            <WizardActions>
              <SecondaryBtn type="button" onClick={openMyChallengesList}>האתגרים שלי</SecondaryBtn>
              <PlayBtn type="button" onClick={goToMenu}><Bubbles/>חזרה לתפריט</PlayBtn>
            </WizardActions>
          </WizardCard>
        </CenterScreen>
      )}

      {screen === "challenge_compare" && compareResult && loadedChallengeMeta && (() => {
        const myName = friendProfile.name || authUser?.displayName || "אני";
        const theirName = loadedChallengeMeta.creatorName || "חבר";
        const myPhoto = friendProfile.photo || authUser?.photoURL || "";
        const theirPhoto = loadedChallengeMeta.creatorPhoto || "";
        const pct = challengeMatchPct(compareResult);
        const ans = compareResult.answeredTotal ?? 0;
        return (
        <CenterScreen>
          <WizardCard>
            <VsHeader>
              <VsPlayer>
                {myPhoto && (myPhoto.startsWith("http") || myPhoto.startsWith("data:")) ? <VsAvatar src={myPhoto} alt="" /> : <VsEmoji>{myPhoto || "🙂"}</VsEmoji>}
                <VsName>{myName}</VsName>
              </VsPlayer>
              <VsBadge>VS</VsBadge>
              <VsPlayer>
                {theirPhoto && (theirPhoto.startsWith("http") || theirPhoto.startsWith("data:")) ? <VsAvatar src={theirPhoto} alt="" /> : <VsEmoji>{theirPhoto || "🙂"}</VsEmoji>}
                <VsName>{theirName}</VsName>
              </VsPlayer>
            </VsHeader>
            <VsMatchPct>{pct}% התאמה</VsMatchPct>
            <VsMatchSub>
              {ans > 0
                ? `${compareResult.matches} התאמות מתוך ${ans} שאלות ששניכם עניתם`
                : "אין שאלות ששניכם עניתם — לא נספרת התאמה"}
            </VsMatchSub>

            {compareResult.sames.length > 0 && (
              <VsSection>
                <VsSectionTitle>💚 במה אתם דומים ({compareResult.sames.length})</VsSectionTitle>
                {compareResult.sames.map((s) => (
                  <VsCard key={s.i} $type="same">
                    <VsOptionsPair>
                      <VsOptionLine>{s.optA}</VsOptionLine>
                      <VsOptionLine>{s.optB}</VsOptionLine>
                    </VsOptionsPair>
                    <VsCardA>שניכם בחרתם: {s.choice || "—"}</VsCardA>
                  </VsCard>
                ))}
              </VsSection>
            )}

            {compareResult.diffs.length > 0 && (
              <VsSection>
                <VsSectionTitle>🔥 במה אתם שונים ({compareResult.diffs.length})</VsSectionTitle>
                {compareResult.diffs.map((d) => (
                  <VsCard key={d.i} $type="diff">
                    <VsOptionsPair>
                      <VsOptionLine>{d.optA}</VsOptionLine>
                      <VsOptionLine>{d.optB}</VsOptionLine>
                    </VsOptionsPair>
                    <VsSplit>
                      <VsSplitSide>{myName}: <strong>{d.friendChoice || "דילוג"}</strong></VsSplitSide>
                      <VsDivider />
                      <VsSplitSide>{theirName}: <strong>{d.creatorChoice || "דילוג"}</strong></VsSplitSide>
                    </VsSplit>
                  </VsCard>
                ))}
              </VsSection>
            )}

            {(compareResult.missed?.length ?? 0) > 0 && (
              <VsSection>
                <VsSectionTitle $variant="missed">⏱️ התפספס ({compareResult.missed.length})</VsSectionTitle>
                {compareResult.missed.map((m) => (
                  <VsCard key={m.i} $type="missed">
                    <VsOptionsPair>
                      <VsOptionLine>{m.optA}</VsOptionLine>
                      <VsOptionLine>{m.optB}</VsOptionLine>
                    </VsOptionsPair>
                    <VsSplit>
                      <VsSplitSide>{myName}: <strong>{m.friendChoice ?? "התפספס"}</strong></VsSplitSide>
                      <VsDivider />
                      <VsSplitSide>{theirName}: <strong>{m.creatorChoice ?? "התפספס"}</strong></VsSplitSide>
                    </VsSplit>
                  </VsCard>
                ))}
              </VsSection>
            )}

            <WizardActions>
              <PlayBtn type="button" onClick={goToMenu}><Bubbles/>סיום</PlayBtn>
            </WizardActions>
          </WizardCard>
        </CenterScreen>
        );
      })()}

      {screen === "menu" && !menuLogoReady && (
        <CenterScreen>
          <MenuSubtitle>טוען…</MenuSubtitle>
        </CenterScreen>
      )}

      {screen === "menu" && menuLogoReady && (
        <MenuScreen>
          <MenuHeroRow>
            <LogoImg src={DILEMA_LOGO_URL} alt="דילמה" />
            <MenuHeadTextBlock>
              <MenuTitle>דילמה</MenuTitle>
              <MenuSubtitle>בחר צד. תהיה מהיר.</MenuSubtitle>
            </MenuHeadTextBlock>
          </MenuHeroRow>

          <MenuSettingsBtnRow>
            <SecondaryBtn type="button" onClick={() => setShowMenuSettings(true)}>
              <BtnIcon>⚙️</BtnIcon>
              הגדרות
            </SecondaryBtn>
          </MenuSettingsBtnRow>

          {authUser ? (
            <UserBar>
              {authUser.photoURL ? <UserBarAvatar src={authUser.photoURL} alt="" /> : <UserBarPlaceholder>{(authUser.displayName || "?")[0]}</UserBarPlaceholder>}
              <UserBarText>{authUser.displayName || authUser.email}</UserBarText>
              <UserBarOut type="button" onClick={signOutGoogle} disabled={authBusy}>התנתק</UserBarOut>
            </UserBar>
          ) : (
            <GoogleSignInBtn type="button" onClick={signInGoogle} disabled={authBusy}>
              <GoogleG aria-hidden="true">G</GoogleG>
              {authBusy ? "מתחבר…" : "התחבר עם Google"}
            </GoogleSignInBtn>
          )}

          <MenuButtons>
            <PlayBtn onClick={startNormalGame}>
              <Bubbles/><PlayBtnIcon>▶</PlayBtnIcon>
              שחק עכשיו
            </PlayBtn>
            <SecondaryBtn onClick={() => {
              if (!authUser) {
                setShowLoginPrompt({onSuccess: () => setScreen("challenge_setup")});
              } else setScreen("challenge_setup");
            }}>
              <BtnIcon>👥</BtnIcon>
              אתגר חבר
            </SecondaryBtn>
            <SecondaryBtn onClick={() => {
              if (!authUser) {
                setShowLoginPrompt({onSuccess: () => openMyChallengesList()});
              } else openMyChallengesList();
            }}>
              <BtnIcon>📋</BtnIcon>
              האתגרים שלי
            </SecondaryBtn>
            <SecondaryBtn onClick={() => setShowHowTo(true)}>
              <BtnIcon>❓</BtnIcon>
              איך משחקים?
            </SecondaryBtn>
            <SecondaryBtn onClick={() => setShowLeaderboard(true)}>
              <BtnIcon>🌐</BtnIcon>
              אתגרים אחרונים
            </SecondaryBtn>
          </MenuButtons>
        </MenuScreen>
      )}

      {screen === "countdown" && (
        <CountdownScreen>
          <CountdownNumber key={countdownNum}>
            {countdownNum === 0 ? "GO!" : countdownNum}
          </CountdownNumber>
        </CountdownScreen>
      )}

      {screen === "game" && (
        <>
          <TopBar>
            <BrandRow>
              <BrandLogo src={DILEMA_LOGO_URL} alt="" />
              <Brand>דילמה</Brand>
            </BrandRow>
          </TopBar>
          <StatsRow>
            <Stat>
              <StatValue>{answered}</StatValue>
              <StatLabel>נענו</StatLabel>
            </Stat>
            <Stat>
              <StatValue>{similarityPct}%</StatValue>
              <StatLabel>דמיון</StatLabel>
            </Stat>
          </StatsRow>

          <Stage>
            <Card className={animState !== "idle" ? animState : undefined}>
              {phase === "question" ? (
                <Buttons>
                  <ChoiceButton $side="left" onClick={() => onPick("A")}>
                    <ChoiceText>{dilemma.a}</ChoiceText>
                  </ChoiceButton>
                  <ChoiceButton $side="right" onClick={() => onPick("B")}>
                    <ChoiceText>{dilemma.b}</ChoiceText>
                  </ChoiceButton>
                </Buttons>
              ) : (
                <Results>
                  <ResultsTitle>{choice ? "התוצאות" : "הזמן נגמר — דילוג"}</ResultsTitle>
                  <ResultRow>
                    <ResultSide $picked={choice === "A"}>
                      <ResultPct>{result.aPct}%</ResultPct>
                      <ResultCaption>{dilemma.a}</ResultCaption>
                    </ResultSide>
                    <Vs>vs</Vs>
                    <ResultSide $picked={choice === "B"}>
                      <ResultPct>{result.bPct}%</ResultPct>
                      <ResultCaption>{dilemma.b}</ResultCaption>
                    </ResultSide>
                  </ResultRow>
                  {choice && (
                    <YouLine>
                      <YouPct>{choice === majority ? Math.max(result.aPct, result.bPct) : Math.min(result.aPct, result.bPct)}%</YouPct>
                      <YouText>בחרו כמוך</YouText>
                    </YouLine>
                  )}
                  <ButtonRow>
                    <NextButton onClick={next}>
                      <Bubbles/>{index + 1 >= nDilemmas ? "סיום" : "לדילמה הבאה ←"}
                    </NextButton>
                    <FinishButton onClick={finishEarly}>דיי אין לי כח יותר</FinishButton>

                  </ButtonRow>
                </Results>
              )}
              {phase === "question" && (
                <TimerRow>
                  <Bar>
                    <BarFill
                      style={{
                        transform: `scaleX(${progress}) translate3d(0, 0, 0)`,
                        WebkitTransform: `scaleX(${progress}) translate3d(0, 0, 0)`,
                      }}
                      $urgent={msLeft < 1500}
                    />
                  </Bar>
                </TimerRow>
              )}
            </Card>
          </Stage>
        </>
      )}

      {screen === "gameover" && (
        <GameOverScreen>
          {personalityInsights && (
            <>
              <PersonalityEmoji>{personalityInsights.emoji}</PersonalityEmoji>
              <GameOverTitle>{personalityInsights.headline}</GameOverTitle>
              <ScoreBig>{similarityPct}%</ScoreBig>
              <ScoreLabel>דמיון לרוב המשתתפים</ScoreLabel>

              {personalityInsights.speedAvgLine || personalityInsights.speedSkippedLine ? (
                <MinoritiesSection>
                  <MinoritiesTitle>⚡ בטיימר</MinoritiesTitle>
                  {personalityInsights.speedAvgLine ? (
                    <MinorityText>{personalityInsights.speedAvgLine}</MinorityText>
                  ) : null}
                  {personalityInsights.speedSkippedLine ? (
                    <MinorityText>{personalityInsights.speedSkippedLine}</MinorityText>
                  ) : null}
                </MinoritiesSection>
              ) : null}

              {personalityInsights.minorities.length > 0 && (
                <MinoritiesSection>
                  <MinoritiesTitle>🦄 איפה את/ה בולט</MinoritiesTitle>
                  {personalityInsights.minorities.slice(0, 5).map((m, i) => (
                    <MinorityItem key={i}>
                      <MinorityText>אתה מהבודדים שמעדיף <MinorityHighlight>{m.yourPick}</MinorityHighlight> על <span>{m.otherPick}</span></MinorityText>
                    </MinorityItem>
                  ))}
                </MinoritiesSection>
              )}
            </>
          )}
          {!personalityInsights && (
            <>
              <GameOverTitle>סיום!</GameOverTitle>
              <ScoreBig>{similarityPct}%</ScoreBig>
              <ScoreLabel>דמיון לרוב</ScoreLabel>
            </>
          )}

          {!submitted && !authUser && (
            <GoogleSignInBtn type="button" onClick={async () => { await signInGoogle(); autoSubmitScore(lastRunStats); }} disabled={authBusy}>
              <GoogleG aria-hidden="true">G</GoogleG>
              {authBusy ? "מתחבר…" : "התחבר עם Google לשמירת הציון"}
            </GoogleSignInBtn>
          )}

          <GameOverButtons>
            <PlayBtn onClick={startNormalGame}><Bubbles/>שחק שוב</PlayBtn>
            <SecondaryBtn onClick={() => {
              if (!authUser) {
                setShowLoginPrompt({onSuccess: () => createChallengeFromLastRun()});
              } else {
                createChallengeFromLastRun();
              }
            }}>
              <BtnIcon>🔗</BtnIcon>
              השווה את עצמך לחבר
            </SecondaryBtn>
            <SecondaryBtn onClick={() => { setShowLeaderboard(true); }}>
              <BtnIcon>🌐</BtnIcon>
              אתגרים אחרונים
            </SecondaryBtn>
            <GhostBtn onClick={() => setScreen("menu")}>חזרה לתפריט</GhostBtn>
          </GameOverButtons>
        </GameOverScreen>
      )}

      {showMenuSettings && screen === "menu" && (
        <Overlay onClick={() => setShowMenuSettings(false)}>
          <OverlayCard onClick={(e) => e.stopPropagation()}>
            <OverlayClose type="button" onClick={() => setShowMenuSettings(false)}>✕</OverlayClose>
            <HowToTitle>הגדרות</HowToTitle>
            <SettingsPopupHint>אורך סיבוב וזמן תשובה לשאלה</SettingsPopupHint>
            <ModeToggleRow>
              <ModeChip type="button" $on={!endless} onClick={() => setEndless(false)} aria-pressed={!endless}>
                15 שאלות (סטנדרטי)
              </ModeChip>
              <ModeChip type="button" $on={endless} onClick={() => setEndless(true)} aria-pressed={endless}>
                לאינסוף ומעבר <span style={{fontSize:"24px", lineHeight:"14px", verticalAlign:"middle"}}>∞</span>
              </ModeChip>
            </ModeToggleRow>
            <ModeToggleRow>
              <ModeChip
                type="button"
                $on={roundMs === ROUND_MS_DEFAULT}
                onClick={() => setRoundMs(ROUND_MS_DEFAULT)}
                aria-pressed={roundMs === ROUND_MS_DEFAULT}
              >
                5 שניות
              </ModeChip>
              <ModeChip
                type="button"
                $on={roundMs === ROUND_MS_SLOW}
                onClick={() => setRoundMs(ROUND_MS_SLOW)}
                aria-pressed={roundMs === ROUND_MS_SLOW}
              >
                7 שניות
              </ModeChip>
            </ModeToggleRow>
            <MenuSettingsDoneRow>
              <GhostBtn type="button" onClick={() => setShowMenuSettings(false)}>סגור</GhostBtn>
            </MenuSettingsDoneRow>
          </OverlayCard>
        </Overlay>
      )}

      {showHowTo && (
        <Overlay onClick={() => setShowHowTo(false)}>
          <OverlayCard onClick={e => e.stopPropagation()}>
            <OverlayClose onClick={() => setShowHowTo(false)}>✕</OverlayClose>
            <HowToTitle>איך משחקים?</HowToTitle>
            <HowToSteps>
              <HowToStep>
                <StepNum>1</StepNum>
                <StepText>רואים שני כפתורים — בחרו מה מתאים לכם יותר</StepText>
              </HowToStep>
              <StepArrow>↓</StepArrow>
              <HowToStep>
                <StepNum>2</StepNum>
                <StepText>בחרו צד תוך <strong>{roundMs / 1000} שניות</strong> — אחרת הזמן נגמר!</StepText>
              </HowToStep>
              <StepArrow>↓</StepArrow>
              <HowToStep>
                <StepNum>3</StepNum>
                <StepText>גלו איך אתם מול העולם</StepText>
              </HowToStep>
              <StepArrow>↓</StepArrow>
              <HowToStep>
                <StepNum>4</StepNum>
                <StepText>שלחו לחבר/ה וגלו תחומי עניין</StepText>
              </HowToStep>
            </HowToSteps>
            <DemoAnim>
              <DemoCard>
                <DemoBtns>
                  <DemoBtn $pop>🍕 פיצה</DemoBtn>
                  <DemoBtn>🍣 סושי</DemoBtn>
                </DemoBtns>
                <DemoTimer />
              </DemoCard>
            </DemoAnim>
            <OverlayCloseBtn onClick={() => setShowHowTo(false)}><Bubbles/>הבנתי!</OverlayCloseBtn>
          </OverlayCard>
        </Overlay>
      )}

      {showLeaderboard && (
        <Overlay onClick={() => setShowLeaderboard(false)}>
          <OverlayCard onClick={e => e.stopPropagation()}>
            <OverlayClose onClick={() => setShowLeaderboard(false)}>✕</OverlayClose>
            <LeaderboardTitle>🌐 אתגרים אחרונים</LeaderboardTitle>
            {challengeFeedData.length === 0 ? (
              <EmptyBoard>עדיין אין זוגות ברשימה.</EmptyBoard>
            ) : (
              <LeaderboardList>
                {challengeFeedData.map((entry) => (
                  <RecentRow key={entry.id} $challenge>
                    <RecentPairBlock>
                      <RecentPairLine>
                        <RecentPairSide $side="creator">
                          {entry.creatorPhoto?.startsWith("http") ? (
                            <RecentPairAvatar src={entry.creatorPhoto} alt="" />
                          ) : (
                            <RecentPairEmoji>{(entry.creatorName || "🙂").trim().slice(0, 1) || "🙂"}</RecentPairEmoji>
                          )}
                          <RecentPairName title={entry.creatorName || ""}>{entry.creatorName || "—"}</RecentPairName>
                        </RecentPairSide>
                        <RecentPairVs>VS</RecentPairVs>
                        <RecentPairSide $side="friend">
                          {entry.friendPhoto?.startsWith("http") ? (
                            <RecentPairAvatar src={entry.friendPhoto} alt="" />
                          ) : (
                            <RecentPairEmoji>{(entry.friendName || "🙂").trim().slice(0, 1) || "🙂"}</RecentPairEmoji>
                          )}
                          <RecentPairName title={entry.friendName || ""}>{entry.friendName || "—"}</RecentPairName>
                        </RecentPairSide>
                      </RecentPairLine>
                      <RecentPairMeta>
                        <RecentPairMatch>{typeof entry.matchPct === "number" ? entry.matchPct : 0}% התאמה</RecentPairMatch>
                        <RecentPairMetaSep aria-hidden>·</RecentPairMetaSep>
                        {entry.bothLine ? (
                          <RecentPairCommon>במשותף: {entry.bothLine}</RecentPairCommon>
                        ) : (
                          <RecentPairCommon $muted>אין בחירה זהה באותה שאלה</RecentPairCommon>
                        )}
                      </RecentPairMeta>
                    </RecentPairBlock>
                  </RecentRow>
                ))}
              </LeaderboardList>
            )}
            <OverlayCloseBtn type="button" onClick={() => setShowLeaderboard(false)}><Bubbles/>סגור</OverlayCloseBtn>
          </OverlayCard>
        </Overlay>
      )}

      {showMyChallenges && (
        <Overlay onClick={() => setShowMyChallenges(false)}>
          <OverlayCard onClick={(e) => e.stopPropagation()}>
            <OverlayClose type="button" onClick={() => setShowMyChallenges(false)}>✕</OverlayClose>
            <LeaderboardTitle>האתגרים שלי</LeaderboardTitle>
            {myChallengesRows.length === 0 ? (
              <EmptyBoard>עדיין אין רשומות — צרו אתגר מ״אתגר חבר״ או ענו על קישור שקיבלתם מחבר</EmptyBoard>
            ) : (
              <MCList>
                {myChallengesRows.map((row) => {
                  const asFriend = row.role === "friend";
                  return (
                  <MCRow key={`${row.id}-${row.role || "creator"}`}>
                    <MCInfo>
                      {asFriend ? (
                        <>
                          <MCRolePill $friend>השתתפתי</MCRolePill>
                          <div><strong>{row.meta.creatorName || "—"}</strong> אתגר אותך</div>
                        </>
                      ) : (
                        <>
                          <MCRolePill>יצרתי</MCRolePill>
                          <div><strong>{row.meta.creatorName}</strong></div>
                        </>
                      )}
                      <MCSmall dir="ltr">{buildChallengeLink(row.id)}</MCSmall>
                    </MCInfo>
                    <MCStatus>
                      {asFriend
                        ? (row.friend ? "הושלם" : "אין תשובות שמורות")
                        : (row.friend ? `נענה: ${row.friend.name}` : "ממתין לחבר")}
                    </MCStatus>
                    {row.friend ? (
                      <MCBtn
                        type="button"
                        onClick={() =>
                          setHistoryDetail({
                            cmp: compareChallengeRuns(row.meta.creatorRounds, row.friend.rounds, row.meta.dilemmas),
                            meta: row.meta,
                            friend: row.friend,
                          })
                        }
                      >
                        השוואה
                      </MCBtn>
                    ) : !asFriend ? (
                      <MCBtn type="button" onClick={() => navigator.clipboard.writeText(buildChallengeLink(row.id)).then(() => showToast("הקישור הועתק! ✓"))}>
                        העתק קישור
                      </MCBtn>
                    ) : null}
                  </MCRow>
                  );
                })}
              </MCList>
            )}
            <OverlayCloseBtn type="button" onClick={() => setShowMyChallenges(false)}><Bubbles/>סגור</OverlayCloseBtn>
          </OverlayCard>
        </Overlay>
      )}

      {historyDetail && (() => {
        const cName = historyDetail.meta.creatorName || "אני";
        const fName = historyDetail.friend.name || "חבר";
        const cPhoto = historyDetail.meta.creatorPhoto || "";
        const fPhoto = historyDetail.friend.photo || "";
        const hPct = challengeMatchPct(historyDetail.cmp);
        const hAns = historyDetail.cmp.answeredTotal ?? 0;
        return (
        <Overlay onClick={() => setHistoryDetail(null)}>
          <OverlayCard onClick={(e) => e.stopPropagation()}>
            <OverlayClose type="button" onClick={() => setHistoryDetail(null)}>✕</OverlayClose>
            <VsHeader>
              <VsPlayer>
                {cPhoto && (cPhoto.startsWith("http") || cPhoto.startsWith("data:")) ? <VsAvatar src={cPhoto} alt="" /> : <VsEmoji>{cPhoto || "🙂"}</VsEmoji>}
                <VsName>{cName}</VsName>
              </VsPlayer>
              <VsBadge>VS</VsBadge>
              <VsPlayer>
                {fPhoto && (fPhoto.startsWith("http") || fPhoto.startsWith("data:")) ? <VsAvatar src={fPhoto} alt="" /> : <VsEmoji>{fPhoto || "🙂"}</VsEmoji>}
                <VsName>{fName}</VsName>
              </VsPlayer>
            </VsHeader>
            <VsMatchPct>{hPct}% התאמה</VsMatchPct>
            <VsMatchSub>
              {hAns > 0
                ? `${historyDetail.cmp.matches} התאמות מתוך ${hAns} שאלות ששניכם עניתם`
                : "אין שאלות ששניכם עניתם — לא נספרת התאמה"}
            </VsMatchSub>

            {historyDetail.cmp.sames?.length > 0 && (
              <VsSection>
                <VsSectionTitle>💚 במה אתם דומים ({historyDetail.cmp.sames.length})</VsSectionTitle>
                {historyDetail.cmp.sames.map((s) => (
                  <VsCard key={s.i} $type="same">
                    <VsOptionsPair>
                      <VsOptionLine>{s.optA}</VsOptionLine>
                      <VsOptionLine>{s.optB}</VsOptionLine>
                    </VsOptionsPair>
                    <VsCardA>שניכם בחרתם: {s.choice || "—"}</VsCardA>
                  </VsCard>
                ))}
              </VsSection>
            )}

            {(historyDetail.cmp.diffs?.length ?? 0) > 0 && (
              <VsSection>
                <VsSectionTitle>🔥 במה אתם שונים ({historyDetail.cmp.diffs.length})</VsSectionTitle>
                {historyDetail.cmp.diffs.map((d) => (
                  <VsCard key={d.i} $type="diff">
                    <VsOptionsPair>
                      <VsOptionLine>{d.optA}</VsOptionLine>
                      <VsOptionLine>{d.optB}</VsOptionLine>
                    </VsOptionsPair>
                    <VsSplit>
                      <VsSplitSide>{cName}: <strong>{d.creatorChoice || "דילוג"}</strong></VsSplitSide>
                      <VsDivider />
                      <VsSplitSide>{fName}: <strong>{d.friendChoice || "דילוג"}</strong></VsSplitSide>
                    </VsSplit>
                  </VsCard>
                ))}
              </VsSection>
            )}

            {(historyDetail.cmp.missed?.length ?? 0) > 0 && (
              <VsSection>
                <VsSectionTitle $variant="missed">⏱️ התפספס ({historyDetail.cmp.missed.length})</VsSectionTitle>
                {historyDetail.cmp.missed.map((m) => (
                  <VsCard key={m.i} $type="missed">
                    <VsOptionsPair>
                      <VsOptionLine>{m.optA}</VsOptionLine>
                      <VsOptionLine>{m.optB}</VsOptionLine>
                    </VsOptionsPair>
                    <VsSplit>
                      <VsSplitSide>{cName}: <strong>{m.creatorChoice ?? "התפספס"}</strong></VsSplitSide>
                      <VsDivider />
                      <VsSplitSide>{fName}: <strong>{m.friendChoice ?? "התפספס"}</strong></VsSplitSide>
                    </VsSplit>
                  </VsCard>
                ))}
              </VsSection>
            )}

            <OverlayCloseBtn type="button" onClick={() => setHistoryDetail(null)}><Bubbles/>סגור</OverlayCloseBtn>
          </OverlayCard>
        </Overlay>
        );
      })()}

      {challengeEndedPopup && (
        <Overlay onClick={() => setChallengeEndedPopup(null)}>
          <OverlayCard onClick={(e) => e.stopPropagation()}>
            <OverlayClose type="button" onClick={() => setChallengeEndedPopup(null)}>✕</OverlayClose>
            <LoginPromptTitle>אתגר זה הסתיים</LoginPromptTitle>
            <LoginPromptText>לאתגר הזה כבר נרשמה תשובה — אפשר לצפות בהשוואה או לפתוח אתגר חדש.</LoginPromptText>
            <ChallengeEndedActions>
              <PlayBtn
                type="button"
                onClick={() => {
                  const {meta, friend} = challengeEndedPopup;
                  setChallengeEndedPopup(null);
                  setHistoryDetail({
                    cmp: compareChallengeRuns(meta.creatorRounds, friend.rounds, meta.dilemmas),
                    meta,
                    friend,
                  });
                }}
              >
                <Bubbles />
                צפייה בתוצאות האתגר
              </PlayBtn>
              <SecondaryBtn
                type="button"
                onClick={() => {
                  setChallengeEndedPopup(null);
                  if (!authUser) {
                    setShowLoginPrompt({onSuccess: () => setScreen("challenge_setup")});
                  } else {
                    setScreen("challenge_setup");
                  }
                }}
              >
                פתיחת אתגר חדש
              </SecondaryBtn>
              <GhostBtn type="button" onClick={() => setChallengeEndedPopup(null)}>
                תפריט ראשי
              </GhostBtn>
            </ChallengeEndedActions>
          </OverlayCard>
        </Overlay>
      )}

      {showLoginPrompt && (
        <Overlay onClick={() => setShowLoginPrompt(null)}>
          <OverlayCard onClick={e => e.stopPropagation()}>
            <OverlayClose onClick={() => setShowLoginPrompt(null)}>✕</OverlayClose>
            <LoginPromptIcon>🔒</LoginPromptIcon>
            <LoginPromptTitle>נדרש חיבור</LoginPromptTitle>
            <LoginPromptText>כדי לשלוח לחבר צריך להתחבר עם Google</LoginPromptText>
            <GoogleSignInBtn type="button" onClick={async () => {
              await signInGoogle();
              const cb = showLoginPrompt?.onSuccess;
              setShowLoginPrompt(null);
              if (cb) cb();
            }} disabled={authBusy}>
              <GoogleG aria-hidden="true">G</GoogleG>
              {authBusy ? "מתחבר…" : "התחבר עם Google"}
            </GoogleSignInBtn>
            <GhostBtn onClick={() => setShowLoginPrompt(null)}>ביטול</GhostBtn>
          </OverlayCard>
        </Overlay>
      )}

      <GameFooter>
        כל הזכויות שמורות לכפיר · בקשות, שאלות:{" "}
        <GameFooterMail
          href="mailto:miller.kfir@gmail.com"
          title="miller.kfir@gmail.com"
          aria-label="שליחת מייל לכפיר — miller.kfir@gmail.com"
        >
          <FooterMailIcon />
        </GameFooterMail>
      </GameFooter>

      {toastMsg && <Toast key={toastMsg}>{toastMsg}</Toast>}
    </FullScreen>
  );
};

/* ─── Animations ─── */

const starWarp = keyframes`
  0% {
    transform: translate(0, 0) scale(0.2);
    opacity: 0;
  }
  5% {
    opacity: 0.9;
  }
  100% {
    transform: translate(var(--dx), var(--dy)) scale(1);
    opacity: 0;
  }
`;

const nebulaShift = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-20px, 10px) scale(1.05); }
  66% { transform: translate(15px, -15px) scale(0.98); }
  100% { transform: translate(0, 0) scale(1); }
`;

const countdownPop = keyframes`
  0% { transform: scale(0.3); opacity: 0; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const cardIn = keyframes`
  from { transform: translateY(10px) scale(0.98); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
`;

const cardOut = keyframes`
  from { transform: translateY(0) scale(1); opacity: 1; }
  to { transform: translateY(-8px) scale(0.985); opacity: 0; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const demoPulse = keyframes`
  0%, 40%, 100% { transform: scale(1); box-shadow: 0 0 0 rgba(34,197,94,0); }
  50% { transform: scale(1.08); box-shadow: 0 0 20px rgba(34,197,94,0.5); }
  60% { transform: scale(0.97); }
  70% { transform: scale(1); }
`;

const demoTimerShrink = keyframes`
  from { width: 100%; }
  to { width: 0; }
`;

const urgentPulse = keyframes`
  0%, 100% { color: #ef4444; }
  50% { color: #fbbf24; }
`;

const logoFloat = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-8px) rotate(-2deg); }
  75% { transform: translateY(-4px) rotate(2deg); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 30px rgba(34,197,94,0.3), 0 0 60px rgba(59,130,246,0.15); }
  50% { box-shadow: 0 0 50px rgba(34,197,94,0.5), 0 0 90px rgba(59,130,246,0.3); }
`;

/** עלייה חלקה — מסלול אחד עם easing, ללא קפיצות בין אחוזי keyframe */
const soapRise = keyframes`
  0% {
    transform: translate3d(0, 10px, 0) scale(0.34);
    opacity: 0;
  }
  5% {
    opacity: 0.62;
  }
  88% {
    opacity: 0.22;
  }
  100% {
    transform: translate3d(var(--bubble-drift, 0px), -338px, 0) scale(1.05);
    opacity: 0;
  }
`;

const BUBBLE_CFGS = [
  { size: 18, left: 10, dur: 2.85, delay: 0, drift: 4 },
  { size: 12, left: 28, dur: 3.35, delay: 0.28, drift: -6 },
  { size: 22, left: 50, dur: 3.05, delay: 0.06, drift: 7 },
  { size: 10, left: 70, dur: 3.55, delay: 0.48, drift: -4 },
  { size: 16, left: 85, dur: 2.95, delay: 0.16, drift: 5 },
  { size: 8,  left: 42, dur: 3.65, delay: 0.62, drift: -3 },
  { size: 14, left: 62, dur: 3.2, delay: 0.36, drift: 6 },
  { size: 20, left: 20, dur: 3.25, delay: 0.52, drift: -2 },
];

/** בועות רק על כפתורים ירוקים — אנימציה רציפה (לא hover) */
const bubbleGreenMixin = css`
  overflow: hidden;
  position: relative;

  & > .bubbles {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
  }

  & > .bubbles span {
    position: absolute;
    bottom: 0;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%,
      rgba(255,255,255,0.5) 0%,
      rgba(255,255,255,0.15) 40%,
      rgba(200,230,255,0.08) 70%,
      transparent 100%);
    border: 1px solid rgba(255,255,255,0.18);
    box-shadow: inset 0 -2px 4px rgba(255,255,255,0.08),
                0 0 6px rgba(200,230,255,0.1);
    opacity: 0;
    will-change: transform, opacity;
    transform: translate3d(0, 10px, 0) scale(0.34);
    animation-name: ${soapRise};
    animation-timing-function: cubic-bezier(0.42, 0, 0.58, 1);
    animation-iteration-count: infinite;
    animation-fill-mode: both;
  }

  ${BUBBLE_CFGS.map((b, i) => `
    & > .bubbles span:nth-child(${i + 1}) {
      width: ${b.size}px;
      height: ${b.size}px;
      left: ${b.left}%;
      --bubble-drift: ${b.drift}px;
      animation-duration: ${b.dur}s;
      animation-delay: ${b.delay}s;
    }
  `).join('')}

  @media (prefers-reduced-motion: reduce) {
    & > .bubbles span {
      animation: none !important;
      opacity: 0;
      transform: none;
    }
  }
`;

const Bubbles = () => (
  <span className="bubbles">
    {BUBBLE_CFGS.map((_, i) => <span key={i} />)}
  </span>
);

/* ─── Layout ─── */

const FullScreen = styled.div`
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #0a0a1a 0%, #0f172a 40%, #0a0a1a 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  overscroll-behavior: none;
  touch-action: manipulation;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const GameFooter = styled.footer`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  text-align: center;
  padding: 4px 12px calc(6px + env(safe-area-inset-bottom, 0px));
  font-size: 10px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.28);
  pointer-events: none;
  & a {
    pointer-events: auto;
  }
`;

const GameFooterMail = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  margin-inline-start: 2px;
  padding: 2px;
  color: rgba(147, 197, 253, 0.55);
  text-decoration: none;
  border-radius: 4px;
  &:hover,
  &:focus-visible {
    color: rgba(147, 197, 253, 0.9);
    background: rgba(147, 197, 253, 0.1);
    outline: none;
  }
  &:active {
    transform: scale(0.95);
  }
`;

const LiveBg = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
`;

const Nebula = styled.div`
  position: absolute;
  inset: -40%;
  background:
    radial-gradient(600px 400px at 30% 25%, rgba(88, 28, 135, 0.18), transparent 60%),
    radial-gradient(500px 500px at 70% 70%, rgba(30, 64, 175, 0.14), transparent 55%),
    radial-gradient(300px 300px at 55% 40%, rgba(34, 197, 94, 0.08), transparent 50%);
  animation: ${nebulaShift} 25s ease-in-out infinite;
`;

const Star = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 ${p => p.$size * 2}px ${p => p.$size * 0.5}px rgba(200, 220, 255, 0.4);
  --dx: ${p => Math.cos(p.$angle * Math.PI / 180) * 55}vw;
  --dy: ${p => Math.sin(p.$angle * Math.PI / 180) * 55}vh;
  animation: ${starWarp} ${p => p.$speed}s linear infinite;
  animation-delay: -${p => p.$delay}s;
`;

/* ─── Menu ─── */

const MenuScreen = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px 24px;
  min-height: 0;
  animation: ${fadeIn} 0.6s ease-out;
`;

const MenuHeroRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 18px;
  flex-wrap: wrap;
`;

const MenuSettingsBtnRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  display: flex;
  justify-content: center;
  margin-bottom: 2px;
`;

const SettingsPopupHint = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  opacity: 0.65;
  text-align: center;
  line-height: 1.35;
`;

const MenuSettingsDoneRow = styled.div`
  margin-top: 16px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const LogoImg = styled.img`
  width: 120px;
  height: 120px;
  flex: 0 0 auto;
  border-radius: 50%;
  animation: ${logoFloat} 4s ease-in-out infinite, ${glowPulse} 3s ease-in-out infinite;
`;

const MenuTitle = styled.div`
  font-family: 'Heebo', sans-serif;
  font-size: clamp(28px, 8vw, 42px);
  font-weight: 950;
  line-height: 1.1;
  background: linear-gradient(135deg, #22c55e, #3b82f6, #a855f7);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite;
`;

const MenuSubtitle = styled.div`
  font-size: 16px;
  opacity: 0.7;
  margin-bottom: 24px;
`;

const MenuHeadTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  text-align: start;

  ${MenuSubtitle} {
    margin-bottom: 0;
  }
`;

const ModeToggleRow = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

const ModeChip = styled.button`
  box-sizing: border-box;
  appearance: none;
  flex: 1;
  border: 1.5px solid ${(p) => (p.$on ? "rgba(34,197,94,0.6)" : "rgba(255,255,255,0.12)")};
  border-radius: 14px;
  padding: 12px 10px;
  cursor: pointer;
  font-weight: 900;
  font-size: 14px;
  text-align: center;
  color: ${(p) => (p.$on ? "#fff" : "rgba(255,255,255,0.55)")};
  background: ${(p) => (p.$on ? "linear-gradient(135deg, rgba(34,197,94,0.25), rgba(59,130,246,0.2))" : "rgba(255,255,255,0.04)")};
  box-shadow: ${(p) => (p.$on ? "0 0 18px rgba(34,197,94,0.15), inset 0 0 12px rgba(34,197,94,0.08)" : "none")};
  transition: all 0.25s ease;
  &:active { transform: scale(0.97); }
`;

const GoogleSignInBtn = styled.button`
  box-sizing: border-box;
  appearance: none;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 14px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 800;
  color: #fff;
  background: rgba(255,255,255,0.08);
  cursor: pointer;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: ${p => p.$compact ? "4px" : "8px"};
  &:disabled { opacity: 0.6; cursor: wait; }
  &:active:not(:disabled) { transform: scale(0.99); }
`;

const GoogleLinked = styled.div`
  font-size: 12px;
  color: rgba(34, 197, 94, 0.85);
  text-align: center;
  margin-bottom: 6px;
  line-height: 1.35;
`;

const GoogleG = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: #fff;
  color: #4285f4;
  font-weight: 900;
  font-size: 14px;
  font-family: 'Product Sans', 'Roboto', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UserBar = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  margin-bottom: 8px;
`;

const UserBarAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserBarPlaceholder = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4285f4, #34a853);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 16px;
`;

const UserBarText = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
`;

const UserBarOut = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: rgba(255,255,255,0.55);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 6px 4px;
  &:hover { color: rgba(255,255,255,0.85); }
`;

const MenuButtons = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
`;

const PlayBtn = styled.button`
  box-sizing: border-box;
  appearance: none;
  border: none;
  border-radius: 16px;
  padding: 18px 24px;
  font-size: 20px;
  font-weight: 900;
  color: #fff;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 8px 30px rgba(34, 197, 94, 0.35);
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: transform 0.15s, box-shadow 0.15s;
  &:active { transform: scale(0.97); }
  ${bubbleGreenMixin}
`;

const PlayBtnIcon = styled.span`
  font-size: 22px;
`;

const SecondaryBtn = styled.button`
  box-sizing: border-box;
  appearance: none;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 14px;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: rgba(255,255,255,0.06);
  backdrop-filter: blur(8px);
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, border-color 0.2s;
  &:active { background: rgba(255,255,255,0.12); }
`;

const BtnIcon = styled.span`
  font-size: 18px;
`;

const GhostBtn = styled.button.attrs({type: "button"})`
  box-sizing: border-box;
  appearance: none;
  border: none;
  background: transparent;
  color: rgba(255,255,255,0.5);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  padding: 10px;
  &:active { color: rgba(255,255,255,0.8); }
`;

/* ─── Countdown ─── */

const CountdownScreen = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CountdownNumber = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 120px;
  font-weight: 950;
  direction: ltr;
  unicode-bidi: isolate;
  background: linear-gradient(135deg, #22c55e, #3b82f6, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${countdownPop} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  text-shadow: 0 0 60px rgba(34,197,94,0.5);
`;

/* ─── Game Top Bar ─── */

const TopBar = styled.div`
  position: relative;
  z-index: 1;
  padding: 14px 16px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BrandLogo = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
`;

const Brand = styled.div`
  font-family: 'Heebo', sans-serif;
  font-weight: 900;
  font-size: 16px;
  opacity: 0.7;
`;

const StatsRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 0 16px 10px;
`;

const Stat = styled.div`
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  padding: 10px 16px;
  min-width: 80px;
  text-align: center;
  flex: 1;
  max-width: 140px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  opacity: 0.5;
  margin-top: 2px;
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 900;
`;

/* ─── Game Stage ─── */

const Stage = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 10px 12px 18px;
`;

const Card = styled.div`
  box-sizing: border-box;
  width: min(${CONTENT_MAX_PX}px, 100%);
  border-radius: 22px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 20px 60px rgba(0,0,0,0.55);
  backdrop-filter: blur(12px);
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-self: center;

  &.enter { animation: ${cardIn} 220ms ease-out both; }
  &.exit { animation: ${cardOut} 180ms ease-in both; }
`;

const TimerRow = styled.div`
  width: 100%;
  margin-top: auto;
  padding-top: 4px;
  direction: ltr;
  unicode-bidi: isolate;
`;

const Bar = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.10);
  overflow: hidden;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  isolation: isolate;
`;

const BarFill = styled.div`
  height: 100%;
  width: 100%;
  transform-origin: left center;
  -webkit-transform-origin: left center;
  background: ${p => p.$urgent
    ? "linear-gradient(90deg, #ef4444, #fbbf24)"
    : "linear-gradient(90deg, #22c55e, #3b82f6, #a855f7)"};
  /* בלי transition על transform — מול rAF זה גורם לריצוד ב-Safari iOS */
  transition: background 0.35s ease;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  will-change: transform;
`;

const Buttons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-items: stretch;
`;

const ChoiceButton = styled.button`
  appearance: none;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 18px;
  padding: 16px 14px;
  background: ${p => p.$side === "left"
    ? "linear-gradient(180deg, rgba(34,197,94,0.22), rgba(34,197,94,0.08))"
    : "linear-gradient(180deg, rgba(168,85,247,0.22), rgba(168,85,247,0.08))"};
  color: #fff;
  text-align: center;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35);
  cursor: pointer;
  transition: transform 0.1s;
  &:active { transform: scale(0.97); }
`;

const ChoiceText = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  padding: 0 6px;
  box-sizing: border-box;
  font-size: clamp(14px, 3.8vw, 22px);
  font-weight: 900;
  line-height: 1.15;
  text-align: center;
`;

/* ─── Results ─── */

const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ResultsTitle = styled.div`
  font-size: 18px;
  font-weight: 900;
  opacity: 0.95;
`;

const ResultRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: center;
`;

const ResultSide = styled.div`
  background: rgba(255,255,255,0.06);
  border: 1px solid ${p => p.$picked ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.10)"};
  border-radius: 16px;
  padding: 14px 12px;
  text-align: center;
  ${p => p.$picked && css`box-shadow: 0 0 20px rgba(34,197,94,0.2);`}
`;

const ResultPct = styled.div`
  font-size: 34px;
  font-weight: 950;
`;

const ResultCaption = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 6px;
`;

const Vs = styled.div`
  font-weight: 900;
  opacity: 0.4;
`;

const YouLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 900;
`;

const YouPct = styled.div`
  font-size: 28px;
  color: #22c55e;
`;

const YouText = styled.div`
  opacity: 0.78;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;

const NextButton = styled.button`
  appearance: none;
  border: 0;
  border-radius: 999px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  font-weight: 900;
  font-size: 16px;
  flex: 1;
  box-shadow: 0 12px 30px rgba(34,197,94,0.3);
  cursor: pointer;
  &:active { transform: scale(0.98); }
  ${bubbleGreenMixin}
`;

const FinishButton = styled.button`
  appearance: none;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 999px;
  padding: 14px 18px;
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.8);
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  &:active { background: rgba(255,255,255,0.12); }
`;

/* ─── Game Over ─── */

const GameOverScreen = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 32px 24px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const PersonalityEmoji = styled.div`
  font-size: 64px;
  line-height: 1;
`;

const GameOverTitle = styled.div`
  box-sizing: border-box;
  font-family: 'Heebo', sans-serif;
  font-size: 22px;
  font-weight: 800;
  text-align: center;
  color: #e2e8f0;
  max-width: ${CONTENT_MAX_PX}px;
  width: 100%;
  line-height: 1.4;
`;

const MinoritiesSection = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MinoritiesTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const MinorityItem = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  &:last-child { border-bottom: none; }
`;

const MinorityText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.8;
`;

const MinorityHighlight = styled.span`
  color: #22c55e;
  font-weight: 700;
`;

const ScoreBig = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 72px;
  font-weight: 950;
  color: #22c55e;
  text-shadow: 0 0 40px rgba(34,197,94,0.4);
`;

const ScoreLabel = styled.div`
  font-size: 16px;
  opacity: 0.6;
  margin-bottom: 4px;
`;

const GameOverStat = styled.div`
  font-size: 14px;
  opacity: 0.5;
  margin-bottom: 16px;
`;

const SubmitSection = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  margin-bottom: 12px;
`;

const SubmitLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  opacity: 0.8;
`;

const NameInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.06);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  outline: none;
  &:focus { border-color: #22c55e; }
  &::placeholder { color: rgba(255,255,255,0.3); }
`;

const PhotoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PhotoLabel = styled.label`
  font-size: 13px;
  opacity: 0.7;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.04);
  display: flex;
  align-items: center;
  gap: 6px;
  &:active { background: rgba(255,255,255,0.1); }
`;

const PhotoInput = styled.input`
  display: none;
`;

const PhotoPreview = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
`;

const SubmitBtn = styled.button`
  appearance: none;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  font-size: 16px;
  font-weight: 900;
  cursor: pointer;
  width: 100%;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:active:not(:disabled) { transform: scale(0.98); }
  ${bubbleGreenMixin}
`;

const SubmittedMsg = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #22c55e;
  margin-bottom: 12px;
`;

const GameOverButtons = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
`;

/* ─── Overlays ─── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.25s ease-out;
`;

const OverlayCard = styled.div`
  box-sizing: border-box;
  background: linear-gradient(180deg, #1a1a2e, #0f0f1a);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 24px;
  padding: 28px 24px;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const OverlayClose = styled.button`
  position: absolute;
  top: 14px;
  left: 14px;
  appearance: none;
  border: none;
  background: rgba(255,255,255,0.08);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OverlayCloseBtn = styled.button`
  appearance: none;
  border: none;
  border-radius: 14px;
  padding: 14px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  font-size: 16px;
  font-weight: 900;
  width: 100%;
  cursor: pointer;
  margin-top: 16px;
  &:active { transform: scale(0.98); }
  ${bubbleGreenMixin}
`;

/* ─── Login Prompt ─── */

const LoginPromptIcon = styled.div`
  font-size: 48px;
  margin-bottom: 4px;
`;

const LoginPromptTitle = styled.div`
  font-size: 22px;
  font-weight: 900;
  color: #e2e8f0;
`;

const LoginPromptText = styled.div`
  font-size: 14px;
  opacity: 0.6;
  text-align: center;
  margin-bottom: 8px;
`;

const ChallengeEndedActions = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  margin: 16px auto 0;
`;

const toastIn = keyframes`
  0% { transform: translate(-50%, 30px); opacity: 0; }
  100% { transform: translate(-50%, 0); opacity: 1; }
`;
const toastOut = keyframes`
  0% { transform: translate(-50%, 0); opacity: 1; }
  100% { transform: translate(-50%, -10px); opacity: 0; }
`;
const Toast = styled.div`
  position: fixed;
  bottom: 36px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15,23,42,0.92);
  color: #e2e8f0;
  padding: 12px 24px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 24px rgba(0,0,0,0.5);
  border: 1px solid rgba(255,255,255,0.08);
  z-index: 9999;
  animation: ${toastIn} 0.3s ease-out, ${toastOut} 0.4s ease-in 2s forwards;
  pointer-events: none;
  white-space: nowrap;
  max-width: 90vw;
  text-align: center;
`;

/* ─── How To Play ─── */

const HowToTitle = styled.div`
  font-size: 22px;
  font-weight: 900;
  text-align: center;
  margin-bottom: 20px;
`;

const HowToSteps = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const HowToStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px;
  background: rgba(255,255,255,0.04);
  border-radius: 12px;
`;

const StepNum = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 14px;
  flex-shrink: 0;
`;

const StepText = styled.div`
  font-size: 14px;
  line-height: 1.4;
  text-align: right;
`;

const StepArrow = styled.div`
  font-size: 18px;
  opacity: 0.3;
`;

const DemoAnim = styled.div`
  margin: 20px 0 8px;
  display: flex;
  justify-content: center;
`;

const DemoCard = styled.div`
  box-sizing: border-box;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  text-align: center;
`;

const DemoBtns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 10px;
`;

const DemoBtn = styled.div`
  padding: 10px;
  border-radius: 10px;
  background: ${p => p.$pop ? "rgba(34,197,94,0.2)" : "rgba(168,85,247,0.2)"};
  border: 1px solid ${p => p.$pop ? "rgba(34,197,94,0.4)" : "rgba(168,85,247,0.3)"};
  font-weight: 800;
  font-size: 14px;
  ${p => p.$pop && css`animation: ${demoPulse} 2.5s ease-in-out infinite;`}
`;

const DemoTimer = styled.div`
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(90deg, #22c55e, #3b82f6, #a855f7);
  animation: ${demoTimerShrink} 3s linear infinite;
`;

/* ─── Leaderboard ─── */

const LeaderboardTitle = styled.div`
  font-size: 22px;
  font-weight: 900;
  text-align: center;
  margin-bottom: 16px;
`;

const EmptyBoard = styled.div`
  text-align: center;
  opacity: 0.5;
  padding: 30px;
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 50vh;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const LeaderboardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${p => p.$rank === 0 ? "rgba(255,215,0,0.08)" : p.$rank === 1 ? "rgba(192,192,192,0.06)" : p.$rank === 2 ? "rgba(205,127,50,0.06)" : "rgba(255,255,255,0.02)"};
  border: 1px solid ${p => p.$rank < 3 ? "rgba(255,215,0,0.12)" : "transparent"};
`;

const Rank = styled.div`
  font-size: ${p => p.$top ? "20px" : "14px"};
  font-weight: 900;
  min-width: 28px;
  text-align: center;
`;

const Avatar = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255,255,255,0.15);
`;

const AvatarPlaceholder = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 14px;
`;

const LBName = styled.div`
  flex: 1;
  font-weight: 700;
  font-size: 14px;
`;

const LBScore = styled.div`
  font-weight: 900;
  font-size: 16px;
  color: #22c55e;
`;

const LBAnswered = styled.div`
  font-size: 10px;
  opacity: 0.45;
  min-width: 72px;
  max-width: 160px;
  text-align: left;
  line-height: 1.3;
`;

const RecentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  &:last-child { border-bottom: none; }
  ${(p) =>
    p.$challenge &&
    css`
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      padding: 8px 0;
    `}
`;

const RecentPairBlock = styled.div`
  width: 100%;
  direction: rtl;
  text-align: right;
`;

const RecentPairLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  box-sizing: border-box;
`;

const RecentPairSide = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  justify-content: ${(p) => (p.$side === "creator" ? "flex-end" : "flex-start")};
`;

const RecentPairAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const RecentPairEmoji = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
`;

const RecentPairName = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const RecentPairVs = styled.span`
  font-size: 18px;
  font-weight: 950;
  color: #f59e0b;
  text-shadow: 0 0 10px rgba(245, 158, 11, 0.45);
  flex-shrink: 0;
  line-height: 1;
`;

const RecentPairMeta = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  line-height: 1.4;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 6px 10px;
  text-align: right;
`;

const RecentPairMetaSep = styled.span`
  flex-shrink: 0;
  opacity: 0.4;
  font-weight: 700;
  user-select: none;
`;

const RecentPairMatch = styled.div`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 800;
  color: #4ade80;
  white-space: nowrap;
`;

const RecentPairCommon = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  opacity: ${(p) => (p.$muted ? 0.42 : 0.82)};
  text-align: right;
  word-break: break-word;
`;

const RecentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RecentName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentInsight = styled.div`
  font-size: 13px;
  opacity: 0.6;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentScore = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #22c55e;
  flex-shrink: 0;
`;

const shareIconFloat = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-5px) scale(1.06); }
`;

const CenterScreen = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
`;

const WizardCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: ${CONTENT_MAX_PX}px;
  background: linear-gradient(180deg, rgba(26,26,46,0.95), rgba(15,15,26,0.98));
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 22px;
  padding: 22px 18px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.5);
`;

const WizardTitle = styled.div`
  font-size: 20px;
  font-weight: 900;
  text-align: center;
  margin-bottom: 14px;
`;

const WizardList = styled.ul`
  margin: 0 0 16px;
  padding: 0 18px 0 0;
  font-size: 14px;
  line-height: 1.55;
  opacity: 0.9;
`;

const WizardLi = styled.li`
  margin-bottom: 8px;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  opacity: 0.65;
  margin: 10px 0 6px;
`;

const AvatarGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 10px;
`;

const AvatarPick = styled.button`
  appearance: none;
  border: 2px solid ${p => p.$on ? "rgba(34,197,94,0.8)" : "rgba(255,255,255,0.12)"};
  background: ${p => p.$on ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.05)"};
  border-radius: 12px;
  width: 44px;
  height: 44px;
  font-size: 22px;
  cursor: pointer;
  transition: transform 0.15s, border-color 0.2s;
  &:active { transform: scale(0.95); }
`;

const WizardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const FriendInvite = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  margin-bottom: 14px;
  font-size: 15px;
`;

const FriendMini = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const FriendEmoji = styled.span`
  font-size: 40px;
  line-height: 1;
`;

const ShareHint = styled.p`
  font-size: 13px;
  line-height: 1.45;
  opacity: 0.75;
  text-align: center;
  margin: 0 0 12px;
`;

const ShareLinkBox = styled.div`
  font-size: 11px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(0,0,0,0.35);
  border: 1px solid rgba(255,255,255,0.1);
  word-break: break-all;
  margin-bottom: 16px;
  opacity: 0.85;
`;

const ShareDock = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 8px;
`;

const ShareIconBtn = styled.button`
  appearance: none;
  border: none;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255,255,255,0.08);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  animation: ${shareIconFloat} 2.2s ease-in-out infinite;
  &:nth-child(2) { animation-delay: 0.25s; }
  &:nth-child(3) { animation-delay: 0.5s; }
  &:active { transform: scale(0.94); }
`;

const CompareBig = styled.div`
  text-align: center;
  font-size: 26px;
  font-weight: 950;
  color: #22c55e;
  margin: 12px 0;
`;

const CompareRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  font-size: 14px;
`;

const DiffList = styled.div`
  max-height: 180px;
  overflow-y: auto;
  margin-top: 8px;
`;

const DiffItem = styled.div`
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(255,255,255,0.04);
  margin-bottom: 6px;
  font-size: 12px;
`;

const DiffChoices = styled.div`
  opacity: 0.7;
  margin-top: 4px;
  font-size: 11px;
`;

/* ─── VS Compare ─── */

const VsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
`;

const VsPlayer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 80px;
`;

const VsAvatar = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255,255,255,0.15);
`;

const VsEmoji = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;

const VsName = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #e2e8f0;
  text-align: center;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VsBadge = styled.div`
  font-size: 22px;
  font-weight: 950;
  color: #f59e0b;
  text-shadow: 0 0 12px rgba(245,158,11,0.5);
  flex-shrink: 0;
`;

const VsMatchPct = styled.div`
  text-align: center;
  font-size: 28px;
  font-weight: 950;
  color: #22c55e;
  margin-top: 4px;
`;

const VsMatchSub = styled.div`
  text-align: center;
  font-size: 13px;
  opacity: 0.5;
  margin-bottom: 12px;
`;

const VsSection = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const VsSectionTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 8px;
  color: ${(p) => (p.$variant === "missed" ? "#94a3b8" : "inherit")};
`;

const VsCard = styled.div`
  background: ${(p) =>
    p.$type === "same"
      ? "rgba(34,197,94,0.08)"
      : p.$type === "missed"
        ? "rgba(148,163,184,0.1)"
        : "rgba(239,68,68,0.08)"};
  border: 1px solid ${(p) =>
    p.$type === "same"
      ? "rgba(34,197,94,0.18)"
      : p.$type === "missed"
        ? "rgba(148,163,184,0.22)"
        : "rgba(239,68,68,0.18)"};
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 8px;
`;

const VsOptionsPair = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 6px;
`;

const VsOptionLine = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #cbd5e1;
  line-height: 1.2;
`;

const VsCardA = styled.div`
  font-size: 12px;
  opacity: 0.7;
`;

const VsSplit = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const VsSplitSide = styled.div`
  flex: 1;
  font-size: 12px;
  opacity: 0.8;
  strong { color: #e2e8f0; }
`;

const VsDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255,255,255,0.12);
  flex-shrink: 0;
`;

const InsightsBox = styled.div`
  background: rgba(34,197,94,0.08);
  border: 1px solid rgba(34,197,94,0.2);
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.45;
`;

const InsightsTitle = styled.div`
  font-weight: 900;
  margin-bottom: 8px;
  color: #86efac;
`;

const InsightsText = styled.div`
  margin-bottom: 6px;
  opacity: 0.9;
`;

const MCList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 55vh;
  overflow-y: auto;
  margin-bottom: 12px;
`;

const MCRolePill = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 999px;
  margin-bottom: 4px;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.88);
  ${(p) => p.$friend && css`
    background: rgba(56, 189, 248, 0.22);
    color: #7dd3fc;
  `}
`;

const MCRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
`;

const MCInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const MCSmall = styled.div`
  font-size: 10px;
  opacity: 0.45;
  word-break: break-all;
  margin-top: 4px;
`;

const MCStatus = styled.div`
  font-size: 12px;
  font-weight: 700;
  opacity: 0.75;
`;

const MCBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 10px;
  padding: 8px 12px;
  background: rgba(34,197,94,0.2);
  color: #fff;
  font-weight: 800;
  font-size: 12px;
  cursor: pointer;
  &:active { transform: scale(0.97); }
`;
